import { v4 as uuid } from "uuid";
import { toast } from "react-toastify";
import {
  Budget,
  BudgetItemType,
  BudgetItemTypeEnum,
  BudgetWideAppliedTaxes,
  CalcOption,
  ExchangeData,
  initialBudgetWideAppliedTaxes,
  ItemTypeListPerCalcOption,
  MainAppliedTaxes,
  NoteAdvance,
  versionInfo,
} from "../../types/budget/budget";
import {
  initialServiceData,
  ServiceData,
  ServiceValues,
} from "../../types/budget/service-item";
import {
  applyEventExchangeToService,
  getEventExchange,
} from "../../utils/exchangeManager";
import { initialItemTypeList } from "./consts";
import {
  calculateAndMapService,
  calculateDetailedTotals,
  calculateDetailedTotalsNegotiation,
  TaxData,
  Totals,
} from "./calc-methods";
import { shouldSkipBudgetRecalcForMetadataOnlyServiceUpdate } from "../../utils/budgetServiceCalcComparison";
import { normalizeGroupName, UNGROUPED_ID } from "../../utils/budget-groups";
import { parseSupplierId } from "../../utils/budget-supplier-id";
import { isServiceVisibleInStage } from "../../utils/budget-stage-visibility";
import {
  isVersionedBillingStage,
  resolveServiceCalcOption,
} from "../../utils/budget-phase-calc-option";

export interface ActiveBudgetAction {
  type:
    | "CHANGE_CALC_TYPE"
    | "NO_ACTIVE_BUDGET"
    | "NEW_ACTIVE_BUDGET"
    | "ADD_SUPPLIER"
    | "UPDATE_SUPPLIER_LIST"
    | "ADD_BUDGET_ITEM"
    | "DELETE_SERVICE"
    | "ADD_SERVICE"
    | "YES_NO_SWITCH_CHANGE"
    | "UPDATE_SERVICE"
    | "UPDATE_FROM_API"
    | "UPDATE_CALC_VALUES"
    | "UPDATE_SERVICES_EXCHANGE"
    | "UPDATE_BASE_VALUES_EXCHANGE"
    | "DELETE_BUDGET_ITEM"
    | "DELETE_SUPPLIER"
    | "CREATE_PAYMENT_INFO"
    | "UPDATE_PAYMENT_INFO"
    | "UPDATE_ANEX_FILES"
    | "CREATE_ANEX_FILES"
    | "UPDATE_BASE_CALC_VALUES"
    | "INIT_BASE_VALUES"
    | "INIT_MAIN_APPLIED_TAXES"
    | "UPDATE_MAIN_APPLIED_TAXES"
    | "NEW_GLOBAL_TAX_DATA"
    | "REORDER_SERVICES"
    | "REORDER_SUPPLIERS"
    | "REORDER_CATEGORIES"
    /**
     * Agrupamento ("Dia 1", "Pré-evento"…). Camada organizacional: nenhuma
     * destas actions altera valor, imposto ou total — o que muda é o
     * particionamento dos serviços. Ver `utils/budget-groups.ts`.
     */
    | "CREATE_GROUP"
    /** Traz os agrupamentos da planilha padrão do cliente; só adiciona. */
    | "ADD_GROUPS_FROM_TEMPLATE"
    | "RENAME_GROUP"
    | "DELETE_GROUP"
    | "DUPLICATE_GROUP"
    | "REORDER_GROUPS"
    | "ADD_ROW_TO_GROUP"
    | "REMOVE_ROW_FROM_GROUP"
    | "MOVE_ROW_TO_GROUP"
    | "MOVE_CATEGORY"
    | "TRANSFER_CATEGORY_SUPPLIER"
    | "TRANSFER_SERVICE_SUPPLIER"
    | "UPDATE_SUPPLIER_DESCRIPTIONS"
    | "SET_NOTE_ADVANCE"
    | "SET_TYPE"
    /** FE-P3-04: swap client temp / pre-move id for server `_id` after POST. */
    | "REPLACE_SERVICE_ID"
    /** FE-P3-04: re-insert a line after failed DELETE (rollback). */
    | "RESTORE_SERVICE"
    /**
     * Recalcula os totais da versão informada sem alterar nenhum dado do
     * orçamento. Usado para reparar em memória agregados legados gravados
     * zerados (`byCalculationType.SUM.SUM`) antes da correção do MMV-2511.
     */
    | "RECALCULATE_TOTALS";
  payload: any;
  versionInfo?: versionInfo;
  silent?: boolean;
}

/**
 * Tira `version` de `deletedInVersions`. Devolve a MESMA referência quando a
 * versão não estava marcada, para o chamador saber que não há o que desfazer.
 */
function withoutDeletedVersion(
  deletedInVersions: number[] | undefined,
  version: number
): number[] | undefined {
  if (!deletedInVersions?.includes(version)) return deletedInVersions;
  return deletedInVersions.filter((v) => v !== version);
}

type MoveCategoryPayload = {
  type: BudgetItemType;
  typeName?: string;
  supplierId: string;
  fromCalcOption: "A" | "B" | "C" | "D";
  toCalcOption: "A" | "B" | "C" | "D";
};

/**
 * No fechamento/negociação a forma de cobrança é da ETAPA: o orçamento
 * (`service.calcOption`, `budgetValues`, listas da origem) permanece na forma
 * original. Sem isso, D→B no fechamento zerava o total do orçamento no sistema
 * e no export — os serviços iam para B e `budgetValuesTotal` continuava em D.
 */
function applyStageScopedMoveCategory(
  budget: Budget,
  payload: MoveCategoryPayload,
  versionInfo: versionInfo
) {
  const currentVersion = versionInfo.version ?? 0;
  const status = (versionInfo.status || "").toUpperCase();

  const zeroFeeIfMovingToD = (taxData: TaxData): TaxData =>
    payload.toCalcOption === "D"
      ? {
          ...taxData,
          fee: { ...taxData.fee, value: 0, currencyValue: 0, valueBRL: 0 },
        }
      : taxData;

  const updatedBudget = {
    ...budget,
    supplierList: [...(budget.supplierList || [])],
    addedItemTypeList: [...(budget.addedItemTypeList || [])],
    budgetServices: [...(budget.budgetServices || [])],
    baseValues: [...(budget.baseValues || [])],
    mainAppliedTaxes: [...(budget.mainAppliedTaxes || [])],
    paymentSolicitation: [...(budget.paymentSolicitation || [])],
    itemTypeList: {
      ...(budget.itemTypeList || initialItemTypeList()),
      [payload.toCalcOption]: {
        ...((budget.itemTypeList || initialItemTypeList())[
          payload.toCalcOption
        ] || {}),
      },
    },
  };

  const matchesMovedService = (service: ServiceData) =>
    service.supplierId === payload.supplierId &&
    service.type === payload.type &&
    resolveServiceCalcOption(service, versionInfo) === payload.fromCalcOption;

  // MMV-2663: o move deixa a categoria de origem para trás, então voltar para
  // uma forma já visitada encontra essa sobra no destino. Sem serviço vigente
  // nela nesta etapa, as taxas da sobra estão velhas e são substituídas pelas
  // da categoria que chega.
  const destinationHasStageServices = updatedBudget.budgetServices.some(
    (service) =>
      service.supplierId === payload.supplierId &&
      service.type === payload.type &&
      resolveServiceCalcOption(service, versionInfo) === payload.toCalcOption
  );

  updatedBudget.budgetServices = updatedBudget.budgetServices.map((service) => {
    if (!matchesMovedService(service)) return service;

    if (status === "APPROVED" || status === "APROVED") {
      return {
        ...service,
        closingValues: service.closingValues && {
          ...service.closingValues,
          calcOption: payload.toCalcOption,
          taxData: zeroFeeIfMovingToD(service.closingValues.taxData),
        },
      };
    }

    const negotiationIndex = currentVersion - 1;
    return {
      ...service,
      negotiationValue: service.negotiationValue?.map((nv, index) =>
        index === negotiationIndex
          ? {
              ...nv,
              calcOption: payload.toCalcOption,
              taxData: zeroFeeIfMovingToD(nv.taxData),
            }
          : nv
      ),
    };
  });

  const hasDestCategory = updatedBudget.addedItemTypeList.some(
    (item) =>
      item.supplierId === payload.supplierId &&
      item.type === payload.type &&
      item.calcOption === payload.toCalcOption
  );
  if (hasDestCategory) {
    // Categoria já existente no destino, mas excluída nesta etapa: receber a
    // categoria movida é uma reintrodução explícita (MMV-2643). Sem isso o
    // serviço ia para o destino e continuava escondido.
    updatedBudget.addedItemTypeList = updatedBudget.addedItemTypeList.map(
      (item) =>
        item.supplierId === payload.supplierId &&
        item.type === payload.type &&
        item.calcOption === payload.toCalcOption
          ? {
              ...item,
              deletedInVersions: withoutDeletedVersion(
                item.deletedInVersions,
                currentVersion
              ),
            }
          : item
    );
  } else {
    const sourceCategory = updatedBudget.addedItemTypeList.find(
      (item) =>
        item.supplierId === payload.supplierId &&
        item.type === payload.type &&
        item.calcOption === payload.fromCalcOption
    );
    updatedBudget.addedItemTypeList = [
      ...updatedBudget.addedItemTypeList,
      {
        ...(sourceCategory || {
          supplierId: payload.supplierId,
          type: payload.type,
        }),
        calcOption: payload.toCalcOption,
        deletedInVersions: undefined,
      },
    ];
  }

  const hasDestSupplier = updatedBudget.supplierList.some(
    (supplier) =>
      supplier.supplierId === payload.supplierId &&
      supplier.calcOption === payload.toCalcOption
  );
  if (hasDestSupplier) {
    // Mesmo caso do fornecedor: o modal promete "será cadastrado na forma de
    // cobrança", então a exclusão da etapa no destino é desfeita (MMV-2643).
    updatedBudget.supplierList = updatedBudget.supplierList.map((supplier) =>
      supplier.supplierId === payload.supplierId &&
      supplier.calcOption === payload.toCalcOption
        ? {
            ...supplier,
            deletedInVersions: withoutDeletedVersion(
              supplier.deletedInVersions,
              currentVersion
            ),
          }
        : supplier
    );
  } else {
    const sourceSupplier = updatedBudget.supplierList.find(
      (supplier) =>
        supplier.supplierId === payload.supplierId &&
        supplier.calcOption === payload.fromCalcOption
    );
    updatedBudget.supplierList = [
      ...updatedBudget.supplierList,
      {
        supplierId: payload.supplierId,
        calcOption: payload.toCalcOption,
        ...(sourceSupplier?.uid ? { uid: uuid() } : {}),
      },
    ];
  }

  updatedBudget.itemTypeList[payload.toCalcOption][payload.type] = true;

  const copyVersionedRows = <
    T extends { supplierId: string; calcOption: string; version?: number }
  >(
    rows: T[],
    categoryKey: keyof T
  ): T[] => {
    const isStageRow = (row: T, calcOption: CalcOption) =>
      row.supplierId === payload.supplierId &&
      row[categoryKey] === (payload.type as T[keyof T]) &&
      row.calcOption === calcOption &&
      (row.version ?? 0) === currentVersion;

    const sources = rows.filter((row) =>
      isStageRow(row, payload.fromCalcOption)
    );
    if (sources.length === 0) return rows;
    const hasDestination = rows.some((row) =>
      isStageRow(row, payload.toCalcOption)
    );
    if (hasDestination && destinationHasStageServices) return rows;
    return [
      ...rows.filter((row) => !isStageRow(row, payload.toCalcOption)),
      ...sources.map((row) => ({ ...row, calcOption: payload.toCalcOption })),
    ];
  };

  updatedBudget.baseValues = copyVersionedRows(
    updatedBudget.baseValues,
    "category"
  );
  updatedBudget.mainAppliedTaxes = copyVersionedRows(
    updatedBudget.mainAppliedTaxes,
    "category"
  );

  const sourcePayment = updatedBudget.paymentSolicitation.find(
    (paymentInfo) =>
      paymentInfo.supplierId === payload.supplierId &&
      paymentInfo.type === payload.type &&
      paymentInfo.calcOption === payload.fromCalcOption
  );
  const hasDestPayment = updatedBudget.paymentSolicitation.some(
    (paymentInfo) =>
      paymentInfo.supplierId === payload.supplierId &&
      paymentInfo.type === payload.type &&
      paymentInfo.calcOption === payload.toCalcOption
  );
  if (sourcePayment && !hasDestPayment) {
    updatedBudget.paymentSolicitation = [
      ...updatedBudget.paymentSolicitation,
      {
        ...sourcePayment,
        _id: uuid(),
        calcOption: payload.toCalcOption,
      },
    ];
  }

  if (updatedBudget.files) {
    const sourceFiles = updatedBudget.files.filter(
      (file: any) =>
        file.supplierId === payload.supplierId &&
        file.type === payload.type &&
        file.calcOption === payload.fromCalcOption
    );
    const destFileKeys = new Set(
      updatedBudget.files
        .filter(
          (file: any) =>
            file.supplierId === payload.supplierId &&
            file.type === payload.type &&
            file.calcOption === payload.toCalcOption
        )
        .map((file: any) => file.fileHash ?? file.name ?? JSON.stringify(file))
    );
    const copiedFiles = sourceFiles
      .filter(
        (file: any) =>
          !destFileKeys.has(file.fileHash ?? file.name ?? JSON.stringify(file))
      )
      .map((file: any) => ({ ...file, calcOption: payload.toCalcOption }));
    if (copiedFiles.length) {
      updatedBudget.files = [...updatedBudget.files, ...copiedFiles];
    }
  }

  return updatedBudget;
}

/**
 * O fornecedor já tem a categoria na forma de destino, e movê-la para lá
 * sobrescreveria a existente. Na negociação/fechamento só conta a categoria que
 * ainda tem serviço vigente na etapa: a que sobrou de um move anterior (a
 * origem nunca é removida, ver `applyStageScopedMoveCategory`) é reaproveitada.
 * Sem isso, voltar para a forma anterior ficava bloqueado (MMV-2663).
 */
export function hasMoveCategoryDuplicate(
  budget: Pick<Budget, "addedItemTypeList" | "budgetServices">,
  payload: Pick<MoveCategoryPayload, "type" | "supplierId" | "toCalcOption">,
  versionInfo?: versionInfo
): boolean {
  const hasDestCategory = (budget.addedItemTypeList || []).some(
    (item) =>
      item.type === payload.type &&
      item.supplierId === payload.supplierId &&
      item.calcOption === payload.toCalcOption
  );
  if (!hasDestCategory) return false;
  if (!isVersionedBillingStage(versionInfo)) return true;
  return (budget.budgetServices || []).some(
    (service) =>
      service.supplierId === payload.supplierId &&
      service.type === payload.type &&
      resolveServiceCalcOption(service, versionInfo) === payload.toCalcOption
  );
}

export function applyMoveCategory(
  budget: Budget,
  payload: MoveCategoryPayload,
  versionInfo?: versionInfo
) {
  if (isVersionedBillingStage(versionInfo)) {
    return applyStageScopedMoveCategory(budget, payload, versionInfo!);
  }

  const baseItemTypeList = initialItemTypeList();
  const mergedItemTypeList = {
    ...baseItemTypeList,
    ...(budget.itemTypeList || {}),
  } as ItemTypeListPerCalcOption;

  (["A", "B", "C", "D"] as const).forEach((option) => {
    mergedItemTypeList[option] = {
      ...(baseItemTypeList as any)[option],
      ...(budget.itemTypeList?.[option] || {}),
    } as any;
  });

  const updatedBudget = {
    ...budget,
    supplierList: [...(budget.supplierList || [])],
    addedItemTypeList: [...(budget.addedItemTypeList || [])],
    budgetServices: [...(budget.budgetServices || [])],
    baseValues: [...(budget.baseValues || [])],
    mainAppliedTaxes: [...(budget.mainAppliedTaxes || [])],
    paymentSolicitation: [...(budget.paymentSolicitation || [])],
    itemTypeList: {
      ...mergedItemTypeList,
      [payload.fromCalcOption]: {
        ...(mergedItemTypeList?.[payload.fromCalcOption] || {}),
      },
      [payload.toCalcOption]: {
        ...(mergedItemTypeList?.[payload.toCalcOption] || {}),
      },
    },
  };

  updatedBudget.addedItemTypeList = updatedBudget.addedItemTypeList.map(
    (item) => {
      if (
        item.supplierId === payload.supplierId &&
        item.type === payload.type &&
        item.calcOption === payload.fromCalcOption
      ) {
        return {
          ...item,
          calcOption: payload.toCalcOption,
        };
      }

      return item;
    }
  );

  // Categorias Tipo D nunca cobram Taxa MM (MMV-2503): ao mover uma categoria
  // para D, zera qualquer fee já configurado (o cálculo já força fee=0 para D,
  // mas isso evita mostrar um valor "fantasma" antes do próximo recálculo).
  const zeroFeeIfMovingToD = (taxData: TaxData): TaxData =>
    payload.toCalcOption === "D"
      ? {
          ...taxData,
          fee: { ...taxData.fee, value: 0, currencyValue: 0, valueBRL: 0 },
        }
      : taxData;

  // O agrupamento acompanha a categoria (MMV-2577).
  //
  // `BudgetGroup` pertence a UMA forma de cobrança, então o `groupId` que o
  // serviço carrega deixa de valer ao mudar de forma: o grupo não existe no
  // destino, o serviço vira órfão e cai em "Sem agrupamento" — com os totais
  // zerados, porque quem soma a linha também não o encontra. E arrastá-lo para
  // um grupo novo não funcionava: a origem "Sem agrupamento" procura serviço
  // com `groupId` ausente, e o dele estava preenchido (apontando para o grupo
  // da forma antiga).
  //
  // A regra é a que o QA pediu: o mesmo agrupamento, pelo NOME, do outro lado.
  // Já existe um com esse nome na forma de destino? usa. Não existe? cria.
  const gruposPorId = new Map(
    (updatedBudget.budgetGroups || []).map((group) => [group.id, group])
  );
  /** `groupId` da origem → `groupId` equivalente no destino. */
  const grupoNoDestino = new Map<string, string>();

  (updatedBudget.budgetServices || []).forEach((service) => {
    if (
      service.supplierId !== payload.supplierId ||
      service.type !== payload.type ||
      service.calcOption !== payload.fromCalcOption ||
      !service.groupId ||
      grupoNoDestino.has(service.groupId)
    ) {
      return;
    }

    const origem = gruposPorId.get(service.groupId);
    // Serviço já órfão antes do move: não há nome para levar junto, e ele segue
    // em "Sem agrupamento" no destino.
    if (!origem) return;

    const existente = (updatedBudget.budgetGroups || []).find(
      (group) =>
        group.calcOption === payload.toCalcOption && group.name === origem.name
    );

    if (existente) {
      grupoNoDestino.set(service.groupId, existente.id);
      return;
    }

    const criado = {
      id: uuid(),
      name: origem.name,
      calcOption: payload.toCalcOption as CalcOption,
    };
    updatedBudget.budgetGroups = [
      ...(updatedBudget.budgetGroups || []),
      criado,
    ];
    grupoNoDestino.set(service.groupId, criado.id);
  });

  updatedBudget.budgetServices = updatedBudget.budgetServices.map((service) => {
    if (
      service.supplierId === payload.supplierId &&
      service.type === payload.type &&
      service.calcOption === payload.fromCalcOption
    ) {
      return {
        ...service,
        calcOption: payload.toCalcOption,
        groupId: service.groupId
          ? grupoNoDestino.get(service.groupId)
          : undefined,
        budgetValues: {
          ...service.budgetValues,
          taxData: zeroFeeIfMovingToD(service.budgetValues.taxData),
        },
        negotiationValue: service.negotiationValue?.map((nv) => ({
          ...nv,
          taxData: zeroFeeIfMovingToD(nv.taxData),
        })),
        closingValues: service.closingValues && {
          ...service.closingValues,
          taxData: zeroFeeIfMovingToD(service.closingValues.taxData),
        },
      };
    }

    return service;
  });

  updatedBudget.baseValues = updatedBudget.baseValues.map((baseValue) => {
    if (
      baseValue.supplierId === payload.supplierId &&
      baseValue.category === payload.type &&
      baseValue.calcOption === payload.fromCalcOption
    ) {
      return {
        ...baseValue,
        calcOption: payload.toCalcOption,
        taxData: zeroFeeIfMovingToD(baseValue.taxData),
      };
    }

    return baseValue;
  });

  updatedBudget.mainAppliedTaxes = updatedBudget.mainAppliedTaxes.map(
    (appliedTax) => {
      if (
        appliedTax.supplierId === payload.supplierId &&
        appliedTax.category === payload.type &&
        appliedTax.calcOption === payload.fromCalcOption
      ) {
        return {
          ...appliedTax,
          calcOption: payload.toCalcOption,
          appliedTaxes:
            payload.toCalcOption === "D"
              ? { ...appliedTax.appliedTaxes, fee: false }
              : appliedTax.appliedTaxes,
        };
      }

      return appliedTax;
    }
  );

  updatedBudget.paymentSolicitation = updatedBudget.paymentSolicitation.map(
    (paymentInfo) => {
      if (
        paymentInfo.supplierId === payload.supplierId &&
        paymentInfo.type === payload.type &&
        paymentInfo.calcOption === payload.fromCalcOption
      ) {
        return {
          ...paymentInfo,
          calcOption: payload.toCalcOption,
        };
      }

      return paymentInfo;
    }
  );

  if (updatedBudget.files) {
    updatedBudget.files = updatedBudget.files.map((file: any) => {
      if (
        file.supplierId === payload.supplierId &&
        file.type === payload.type &&
        file.calcOption === payload.fromCalcOption
      ) {
        return { ...file, calcOption: payload.toCalcOption };
      }
      return file;
    });
  }

  const hasTargetSupplier = updatedBudget.supplierList.some(
    (supplier) =>
      supplier.supplierId === payload.supplierId &&
      supplier.calcOption === payload.toCalcOption
  );

  if (!hasTargetSupplier) {
    updatedBudget.supplierList = [
      ...updatedBudget.supplierList,
      {
        supplierId: payload.supplierId,
        calcOption: payload.toCalcOption,
      },
    ];
  }

  const hasItemsInSourceOption = updatedBudget.addedItemTypeList.some(
    (item) =>
      item.supplierId === payload.supplierId &&
      item.calcOption === payload.fromCalcOption
  );

  if (!hasItemsInSourceOption) {
    updatedBudget.supplierList = updatedBudget.supplierList.filter(
      (supplier) =>
        !(
          supplier.supplierId === payload.supplierId &&
          supplier.calcOption === payload.fromCalcOption
        )
    );
  }

  updatedBudget.itemTypeList[payload.toCalcOption][payload.type] = true;

  const hasCategoryInSource = updatedBudget.addedItemTypeList.some(
    (item) =>
      item.type === payload.type && item.calcOption === payload.fromCalcOption
  );

  updatedBudget.itemTypeList[payload.fromCalcOption][payload.type] =
    hasCategoryInSource;

  return updatedBudget;
}

type SupplierRenamePayload = {
  currentId: string;
  newId: string;
  calcOption: string;
};

/**
 * Formas de cobrança para onde o serviço foi movido em alguma etapa. Desde o
 * MMV-2607, "Mover" no fechamento/negociação só grava a forma no bloco de
 * valores da etapa; `service.calcOption` continua sendo a do orçamento.
 */
function stageMoveTargets(service: ServiceData): CalcOption[] {
  const targets = new Set<CalcOption>();
  const add = (calcOption?: CalcOption) => {
    if (calcOption && calcOption !== service.calcOption)
      targets.add(calcOption);
  };
  add(service.budgetValues?.calcOption);
  service.negotiationValue?.forEach((nv) => add(nv?.calcOption));
  add(service.closingValues?.calcOption);
  return Array.from(targets);
}

/** Destinos `(type, calcOption)` das categorias do fornecedor movidas numa etapa. */
function stageMoveDestinations(
  budget: Budget,
  payload: SupplierRenamePayload
): { type: string; calcOption: CalcOption }[] {
  const keys = new Map<string, { type: string; calcOption: CalcOption }>();
  for (const service of budget.budgetServices || []) {
    if (
      service.supplierId !== payload.currentId ||
      service.calcOption !== payload.calcOption
    ) {
      continue;
    }
    for (const calcOption of stageMoveTargets(service)) {
      keys.set(`${service.type}|${calcOption}`, {
        type: service.type,
        calcOption,
      });
    }
  }
  return Array.from(keys.values());
}

/**
 * MMV-2626: trocar o fornecedor de um card renomeia os serviços pela
 * identidade (`service.calcOption`), inclusive os que foram movidos para outra
 * forma numa etapa. As entradas de destino desse move (categoria, taxas,
 * card em `supplierList`) precisam ir junto; sem isso o serviço fica sem card
 * para desenhá-lo e continua no xlsx.
 *
 * Devolve o motivo do bloqueio quando o modelo não comporta a troca — uma
 * categoria só tem uma identidade `(fornecedor, tipo, forma)`:
 * - a troca é feita no card de DESTINO de um move: o serviço também pertence ao
 *   card de origem e não pode ter dois fornecedores;
 * - a categoria de destino também tem serviço próprio do fornecedor antigo,
 *   ou o fornecedor novo já tem essa categoria nessa forma.
 */
export function findStageMoveSupplierRenameConflict(
  budget: Budget,
  payload: SupplierRenamePayload
): string | null {
  const services = budget.budgetServices || [];
  const movedIntoCard = services.find(
    (service) =>
      service.supplierId === payload.currentId &&
      service.calcOption !== payload.calcOption &&
      stageMoveTargets(service).includes(payload.calcOption as CalcOption)
  );
  if (movedIntoCard) {
    const label =
      BudgetItemTypeEnum[
        movedIntoCard.type as keyof typeof BudgetItemTypeEnum
      ] ?? movedIntoCard.type;
    return `A categoria ${label} foi movida de ${movedIntoCard.calcOption} para ${payload.calcOption} numa etapa. Para trocar o fornecedor dela, use Mover → Transferir para outro fornecedor na categoria.`;
  }

  for (const { type, calcOption } of stageMoveDestinations(budget, payload)) {
    const label =
      BudgetItemTypeEnum[type as keyof typeof BudgetItemTypeEnum] ?? type;
    const sharedWithOtherServices = services.some(
      (service) =>
        service.supplierId === payload.currentId &&
        service.type === type &&
        service.calcOption !== payload.calcOption &&
        (service.calcOption === calcOption ||
          stageMoveTargets(service).includes(calcOption))
    );
    const targetAlreadyHasCategory = (budget.addedItemTypeList || []).some(
      (item) =>
        item.supplierId === payload.newId &&
        item.type === type &&
        item.calcOption === calcOption
    );
    if (sharedWithOtherServices || targetAlreadyHasCategory) {
      return `A categoria ${label} deste fornecedor foi movida para ${calcOption} numa etapa e lá já existe outra ${label}. Mova a categoria de volta para ${payload.calcOption} antes de trocar o fornecedor.`;
    }
  }

  return null;
}

/**
 * Motivo para recusar a troca de fornecedor de um card, ou `null` se ela pode
 * seguir. É a mesma checagem do reducer, exposta para o card recusar ANTES de
 * despachar — o seletor de fornecedor precisa saber para voltar ao valor real.
 *
 * Só há o que checar quando os serviços trocam de dono, isto é, quando nenhum
 * outro card usa o id antigo nesta forma (ver UPDATE_SUPPLIER_LIST).
 */
export function getSupplierRenameBlockReason(
  budget: Pick<Budget, "supplierList" | "budgetServices" | "addedItemTypeList">,
  payload: SupplierRenamePayload & { supplierListIndex?: number },
  versionInfo?: versionInfo
): string | null {
  if (!payload.currentId || payload.currentId === payload.newId) return null;
  const supplierList = budget.supplierList || [];
  const supplierIdx =
    payload.supplierListIndex ??
    supplierList.findIndex(
      (el) =>
        el.supplierId === payload.currentId &&
        el.calcOption === payload.calcOption
    );
  const renamesServices = !supplierList.some(
    (el, idx) =>
      idx !== supplierIdx &&
      el.supplierId === payload.currentId &&
      el.calcOption === payload.calcOption
  );
  if (!renamesServices) return null;
  if (isStageScopedSupplierCardSwap(budget as Budget, payload, versionInfo)) {
    return findStageScopedSupplierCardSwapBlockReason(
      budget as Budget,
      payload,
      versionInfo!
    );
  }
  return findStageMoveSupplierRenameConflict(budget as Budget, payload);
}

/**
 * Leva as entradas de destino dos moves por etapa para o fornecedor novo.
 * Rodar DEPOIS de renomear os serviços (e só quando eles foram renomeados);
 * `destinations` vem de `stageMoveDestinations` calculado ANTES da troca.
 */
function carryStageMoveDestinationsOnSupplierRename(
  budget: Budget,
  payload: SupplierRenamePayload,
  destinations: { type: string; calcOption: CalcOption }[]
) {
  if (destinations.length === 0) return budget;

  const isDestination = (
    supplierId: string | undefined,
    type: string | undefined,
    calcOption: string | undefined
  ) =>
    supplierId === payload.currentId &&
    destinations.some(
      (dest) => dest.type === type && dest.calcOption === calcOption
    );

  const updatedBudget = {
    ...budget,
    addedItemTypeList: (budget.addedItemTypeList || []).map((item) =>
      isDestination(item.supplierId, item.type, item.calcOption)
        ? { ...item, supplierId: payload.newId }
        : item
    ),
    baseValues: (budget.baseValues || []).map((row) =>
      isDestination(row.supplierId, row.category, row.calcOption)
        ? { ...row, supplierId: payload.newId }
        : row
    ),
    mainAppliedTaxes: (budget.mainAppliedTaxes || []).map((row) =>
      isDestination(row.supplierId, row.category, row.calcOption)
        ? { ...row, supplierId: payload.newId }
        : row
    ),
    paymentSolicitation: (budget.paymentSolicitation || []).map((row) =>
      isDestination(row.supplierId, row.type, row.calcOption)
        ? { ...row, supplierId: payload.newId }
        : row
    ),
    supplierList: [...(budget.supplierList || [])],
  };

  if (updatedBudget.files) {
    updatedBudget.files = updatedBudget.files.map((file: any) =>
      isDestination(file.supplierId, file.type, file.calcOption)
        ? { ...file, supplierId: payload.newId }
        : file
    );
  }

  const destinationOptions = Array.from(
    new Set(destinations.map((dest) => dest.calcOption))
  );
  for (const calcOption of destinationOptions) {
    const hasNewCard = updatedBudget.supplierList.some(
      (supplier) =>
        supplier.supplierId === payload.newId &&
        supplier.calcOption === calcOption
    );
    if (!hasNewCard) {
      updatedBudget.supplierList.push({
        supplierId: payload.newId,
        calcOption,
        uid: uuid(),
      });
    }

    // O card antigo no destino foi criado pelo move; se nada mais o usa, sai.
    const oldCardStillUsed =
      updatedBudget.addedItemTypeList.some(
        (item) =>
          item.supplierId === payload.currentId &&
          item.calcOption === calcOption
      ) ||
      (updatedBudget.budgetServices || []).some(
        (service) =>
          service.supplierId === payload.currentId &&
          (service.calcOption === calcOption ||
            stageMoveTargets(service).includes(calcOption))
      );
    if (!oldCardStillUsed) {
      updatedBudget.supplierList = updatedBudget.supplierList.filter(
        (supplier) =>
          !(
            supplier.supplierId === payload.currentId &&
            supplier.calcOption === calcOption
          )
      );
    }
  }

  return updatedBudget;
}

/**
 * Renomeia o fornecedor nas células `byCalculationType[forma][tipo][]` de uma
 * árvore de totais. Os valores não mudam — só o dono.
 */
function renameSupplierInTotalsTree<T>(
  tree: T,
  currentId: string,
  newId: string,
  matches: (calcOption: string, type: string) => boolean
): T {
  const byCalculationType = (tree as any)?.byCalculationType;
  if (!byCalculationType || typeof byCalculationType !== "object") return tree;

  let nextByCalculationType: Record<string, any> | null = null;
  for (const calcOption of Object.keys(byCalculationType)) {
    if (calcOption === "SUM") continue;
    const block = byCalculationType[calcOption];
    if (!block || typeof block !== "object") continue;

    let nextBlock: Record<string, any> | null = null;
    for (const type of Object.keys(block)) {
      const cells = block[type];
      if (!Array.isArray(cells) || !matches(calcOption, type)) continue;
      if (!cells.some((cell: any) => cell?.supplierId === currentId)) continue;
      const updatedBlock: Record<string, any> = nextBlock ?? { ...block };
      updatedBlock[type] = cells.map((cell: any) =>
        cell?.supplierId === currentId ? { ...cell, supplierId: newId } : cell
      );
      nextBlock = updatedBlock;
    }
    if (nextBlock) {
      const updatedByCalculationType: Record<string, any> =
        nextByCalculationType ?? { ...byCalculationType };
      updatedByCalculationType[calcOption] = nextBlock;
      nextByCalculationType = updatedByCalculationType;
    }
  }

  return nextByCalculationType
    ? ({ ...(tree as any), byCalculationType: nextByCalculationType } as T)
    : tree;
}

/**
 * MMV-2626: a troca de fornecedor muda o dono dos serviços em TODAS as
 * versões, mas o reducer só recalcula a árvore da etapa aberta. Sem renomear
 * as demais, a comparação (ex.: fechamento × orçamento) lia a árvore da outra
 * versão ainda com o id antigo — o card mostrava o valor inteiro como
 * diferença até alguém recalcular aquela versão.
 */
function renameSupplierInAllTotalsTrees(
  budget: Budget,
  payload: SupplierRenamePayload,
  destinations: { type: string; calcOption: CalcOption }[]
): Budget {
  const matches = (calcOption: string, type: string) =>
    calcOption === payload.calcOption ||
    destinations.some(
      (dest) => dest.calcOption === calcOption && dest.type === type
    );
  const rename = <T>(tree: T) =>
    renameSupplierInTotalsTree(tree, payload.currentId, payload.newId, matches);

  return {
    ...budget,
    budgetValuesTotal: rename(budget.budgetValuesTotal),
    negociationValuesTotal: Array.isArray(budget.negociationValuesTotal)
      ? budget.negociationValuesTotal.map(rename)
      : budget.negociationValuesTotal,
    closingValuesTotal: rename(budget.closingValuesTotal),
  };
}

export type TransferCategorySupplierPayload = {
  type: BudgetItemType;
  typeName?: string;
  calcOption: "A" | "B" | "C" | "D";
  /** Id interno (pode vir sufixado com `|dup|` / `-<calcOption>-<n>`) do fornecedor de origem. */
  fromSupplierId: string;
  /** Id interno JÁ resolvido para o destino (ver `resolveNewSupplierId`). */
  toSupplierId: string;
  toSupplierName: string;
  /**
   * Informativo (a UI usa para decidir os avisos de confirmação): o destino
   * ainda não está na `supplierList` desta forma de cobrança. O reducer não
   * confia nesta flag — ele checa a lista e só insere se realmente faltar,
   * mantendo a ação idempotente.
   */
  createTargetSupplier?: boolean;
};

/**
 * Transfere uma categoria de um fornecedor para outro DENTRO da mesma forma de
 * cobrança. É o espelho de `applyMoveCategory`, trocando `supplierId` em vez de
 * `calcOption`, com três diferenças de regra:
 *
 * 1. O fornecedor de origem NUNCA é removido da `supplierList`, mesmo que fique
 *    sem nenhuma categoria — requisito explícito do card. Ele volta a aparecer
 *    como uma linha vazia (`existsInVersion` mantém visível quem não tem
 *    serviço algum, ver `utils/budget-stage-visibility.ts`).
 * 2. `itemTypeList` não é tocado: é indexado por forma de cobrança, não por
 *    fornecedor, e a forma de cobrança não muda aqui.
 * 3. Nada de `zeroFeeIfMovingToD` — zerar a Taxa MM é regra de Tipo D
 *    (MMV-2503), disparada por mudança de forma de cobrança. Uma transferência
 *    preserva valores, taxas e impostos exatamente como estavam.
 *
 * `baseValues` e `mainAppliedTaxes` são reescritos em TODAS as versões: eles
 * carregam `version`, e deixar as versões de negociação/fechamento apontando
 * para o fornecedor antigo deixaria a categoria sem taxas nessas etapas.
 */
export function applyTransferCategorySupplier(
  budget: Budget,
  payload: TransferCategorySupplierPayload,
  versionInfo?: versionInfo
) {
  const { type, calcOption, fromSupplierId, toSupplierId } = payload;

  if (!fromSupplierId || !toSupplierId || fromSupplierId === toSupplierId) {
    return budget;
  }

  if (isVersionedBillingStage(versionInfo)) {
    return applyStageScopedTransferCategorySupplier(
      budget,
      payload,
      versionInfo!
    );
  }

  const updatedBudget = {
    ...budget,
    supplierList: [...(budget.supplierList || [])],
    addedItemTypeList: [...(budget.addedItemTypeList || [])],
    budgetServices: [...(budget.budgetServices || [])],
    baseValues: [...(budget.baseValues || [])],
    mainAppliedTaxes: [...(budget.mainAppliedTaxes || [])],
    paymentSolicitation: [...(budget.paymentSolicitation || [])],
  };

  updatedBudget.addedItemTypeList = updatedBudget.addedItemTypeList.map(
    (item) =>
      item.supplierId === fromSupplierId &&
      item.type === type &&
      item.calcOption === calcOption
        ? { ...item, supplierId: toSupplierId }
        : item
  );

  // `_id`, `versions` e os três blocos de valores (budgetValues /
  // negotiationValue / closingValues) são preservados intactos: a transferência
  // não altera nenhum valor, só a quem a categoria pertence.
  updatedBudget.budgetServices = updatedBudget.budgetServices.map((service) =>
    service.supplierId === fromSupplierId &&
    service.type === type &&
    service.calcOption === calcOption
      ? {
          ...service,
          supplierId: toSupplierId,
          supplierName: payload.toSupplierName,
        }
      : service
  );

  updatedBudget.baseValues = updatedBudget.baseValues.map((baseValue) =>
    baseValue.supplierId === fromSupplierId &&
    baseValue.category === type &&
    baseValue.calcOption === calcOption
      ? { ...baseValue, supplierId: toSupplierId }
      : baseValue
  );

  updatedBudget.mainAppliedTaxes = updatedBudget.mainAppliedTaxes.map(
    (appliedTax) =>
      appliedTax.supplierId === fromSupplierId &&
      appliedTax.category === type &&
      appliedTax.calcOption === calcOption
        ? { ...appliedTax, supplierId: toSupplierId }
        : appliedTax
  );

  updatedBudget.paymentSolicitation = updatedBudget.paymentSolicitation.map(
    (paymentInfo) =>
      paymentInfo.supplierId === fromSupplierId &&
      paymentInfo.type === type &&
      paymentInfo.calcOption === calcOption
        ? { ...paymentInfo, supplierId: toSupplierId }
        : paymentInfo
  );

  if (updatedBudget.files) {
    updatedBudget.files = updatedBudget.files.map((file: any) =>
      file.supplierId === fromSupplierId &&
      file.type === type &&
      file.calcOption === calcOption
        ? { ...file, supplierId: toSupplierId }
        : file
    );
  }

  const hasTargetSupplier = updatedBudget.supplierList.some(
    (supplier) =>
      supplier.supplierId === toSupplierId && supplier.calcOption === calcOption
  );

  if (!hasTargetSupplier) {
    updatedBudget.supplierList = [
      ...updatedBudget.supplierList,
      {
        supplierId: toSupplierId,
        calcOption,
      },
    ];
  }

  return updatedBudget;
}

/**
 * Serviços que o card mostra na etapa e as formas em que a categoria deles
 * existe: a do card, a do orçamento (`service.calcOption`) e as de outros
 * moves por etapa.
 */
function planStageScopedTransfer(
  budget: Budget,
  payload: TransferCategorySupplierPayload,
  versionInfo: versionInfo
) {
  const moved = new Set<ServiceData>(
    (budget.budgetServices || []).filter(
      (service) =>
        service.supplierId === payload.fromSupplierId &&
        service.type === payload.type &&
        resolveServiceCalcOption(service, versionInfo) === payload.calcOption
    )
  );
  const forms = new Set<CalcOption>([payload.calcOption]);
  moved.forEach((service) => {
    forms.add(service.calcOption as CalcOption);
    stageMoveTargets(service).forEach((calcOption) => forms.add(calcOption));
  });
  return { moved, forms: Array.from(forms) };
}

/**
 * Motivo para recusar a transferência na negociação/fechamento, ou `null`.
 * Os serviços levam a categoria de TODAS as formas em que ela existe (ver
 * `applyStageScopedTransferCategorySupplier`); se o destino já tem a mesma
 * categoria numa delas, as duas virariam uma só e o valor do orçamento mudaria.
 * A forma do próprio card é checada pela UI antes, com mensagem própria.
 */
export function findTransferCategoryBlockReason(
  budget: Budget,
  payload: TransferCategorySupplierPayload,
  versionInfo?: versionInfo
): string | null {
  if (!isVersionedBillingStage(versionInfo)) return null;
  const { forms } = planStageScopedTransfer(budget, payload, versionInfo!);
  const label =
    BudgetItemTypeEnum[payload.type as keyof typeof BudgetItemTypeEnum] ??
    payload.type;
  const conflict = forms.find(
    (calcOption) =>
      calcOption !== payload.calcOption &&
      (budget.addedItemTypeList || []).some(
        (item) =>
          item.supplierId === payload.toSupplierId &&
          item.type === payload.type &&
          item.calcOption === calcOption
      )
  );
  if (!conflict) return null;
  return `A categoria ${label} também existe na forma ${conflict} (em outra etapa) e o fornecedor de destino já tem ${label} lá. Mova uma das duas antes de transferir.`;
}

/**
 * MMV-2663: na negociação/fechamento o card mostra os serviços pela forma da
 * ETAPA, mas eles continuam identificados pela forma do orçamento
 * (`service.calcOption`, ver `applyStageScopedMoveCategory`). Transferir
 * casando pela identidade deixava o serviço movido no fornecedor antigo e só a
 * categoria e as taxas iam para o novo — o card de origem ficava com o valor e
 * sem categoria, e o total perdia as taxas.
 *
 * Aqui os serviços são os do card na etapa, e cada forma em que a categoria
 * existe vai junto para o fornecedor novo. Se o fornecedor antigo ainda tem
 * outro serviço da categoria numa dessas formas, ela é copiada em vez de
 * movida. Como na transferência comum, o card antigo nunca é removido.
 */
function applyStageScopedTransferCategorySupplier(
  budget: Budget,
  payload: TransferCategorySupplierPayload,
  versionInfo: versionInfo
) {
  const { type, fromSupplierId, toSupplierId } = payload;

  const blockReason = findTransferCategoryBlockReason(
    budget,
    payload,
    versionInfo
  );
  if (blockReason) {
    toast.error(blockReason);
    return budget;
  }

  const { moved, forms } = planStageScopedTransfer(
    budget,
    payload,
    versionInfo
  );

  const budgetServices = (budget.budgetServices || []).map((service) =>
    moved.has(service)
      ? {
          ...service,
          supplierId: toSupplierId,
          supplierName: payload.toSupplierName,
        }
      : service
  );

  const stillUsedByOrigin = (calcOption: CalcOption) =>
    budgetServices.some(
      (service) =>
        service.supplierId === fromSupplierId &&
        service.type === type &&
        (service.calcOption === calcOption ||
          stageMoveTargets(service).includes(calcOption))
    );
  const copiedForms = forms.filter(stillUsedByOrigin);
  const movedForms = forms.filter(
    (calcOption) => !copiedForms.includes(calcOption)
  );

  const transferRows = <
    T extends { supplierId?: string; calcOption?: string; _id?: string }
  >(
    rows: T[],
    rowType: (row: T) => string | undefined,
    freshId = false
  ): T[] => {
    const isOrigin = (row: T, calcOptions: CalcOption[]) =>
      row.supplierId === fromSupplierId &&
      rowType(row) === type &&
      calcOptions.includes(row.calcOption as CalcOption);
    const renamed = rows.map((row) =>
      isOrigin(row, movedForms) ? { ...row, supplierId: toSupplierId } : row
    );
    const copies = rows
      .filter((row) => isOrigin(row, copiedForms))
      .map((row) => ({
        ...row,
        supplierId: toSupplierId,
        ...(freshId ? { _id: uuid() } : {}),
      }));
    return copies.length ? [...renamed, ...copies] : renamed;
  };

  const updatedBudget: Budget = {
    ...budget,
    budgetServices,
    addedItemTypeList: transferRows(
      budget.addedItemTypeList || [],
      (item) => item.type
    ),
    baseValues: transferRows(budget.baseValues || [], (row) => row.category),
    mainAppliedTaxes: transferRows(
      budget.mainAppliedTaxes || [],
      (row) => row.category
    ),
    paymentSolicitation: transferRows(
      budget.paymentSolicitation || [],
      (row) => row.type,
      true
    ),
    supplierList: [...(budget.supplierList || [])],
  };

  if (updatedBudget.files) {
    updatedBudget.files = transferRows(
      updatedBudget.files as any[],
      (file) => file.type
    );
  }

  for (const calcOption of forms) {
    const hasTargetSupplier = updatedBudget.supplierList.some(
      (supplier) =>
        supplier.supplierId === toSupplierId &&
        supplier.calcOption === calcOption
    );
    if (!hasTargetSupplier) {
      updatedBudget.supplierList.push({ supplierId: toSupplierId, calcOption });
    }
  }

  // O reducer só recalcula a árvore da etapa aberta; as das outras versões
  // continuariam com o id antigo e a comparação mostraria o valor como
  // diferença (mesmo motivo de `renameSupplierInAllTotalsTrees`).
  const rename = <T>(tree: T) =>
    renameSupplierInTotalsTree(
      tree,
      fromSupplierId,
      toSupplierId,
      (calcOption, cellType) =>
        cellType === type && movedForms.includes(calcOption as CalcOption)
    );
  updatedBudget.budgetValuesTotal = rename(updatedBudget.budgetValuesTotal);
  if (Array.isArray(updatedBudget.negociationValuesTotal)) {
    updatedBudget.negociationValuesTotal =
      updatedBudget.negociationValuesTotal.map(rename);
  }
  updatedBudget.closingValuesTotal = rename(updatedBudget.closingValuesTotal);

  return updatedBudget;
}

/**
 * A troca pelo seletor do card vira transferência por categoria quando, na
 * negociação/fechamento, o card recebeu categoria movida de outra forma. A
 * troca comum renomeia pela identidade (`service.calcOption`) e não alcança o
 * serviço movido — por isso ela era recusada (MMV-2626) e o usuário ficava sem
 * como definir o fornecedor no fechamento (MMV-2663).
 */
function isStageScopedSupplierCardSwap(
  budget: Budget,
  payload: SupplierRenamePayload,
  versionInfo?: versionInfo
): boolean {
  if (!isVersionedBillingStage(versionInfo)) return false;
  return (budget.budgetServices || []).some(
    (service) =>
      service.supplierId === payload.currentId &&
      service.calcOption !== payload.calcOption &&
      stageMoveTargets(service).includes(payload.calcOption as CalcOption)
  );
}

/**
 * A troca vai pelo caminho de `applyStageScopedSupplierCardSwap`: é etapa
 * versionada, os serviços trocam de dono (nenhum outro card usa o id antigo
 * nesta forma) e o card recebeu categoria movida. O dispatch usa isto para
 * gravar o documento inteiro — como mover/transferir categoria.
 */
export function usesStageScopedSupplierCardSwap(
  budget: Pick<Budget, "supplierList" | "budgetServices">,
  payload: SupplierRenamePayload & { supplierListIndex?: number },
  versionInfo?: versionInfo
): boolean {
  if (!payload.currentId || payload.currentId === payload.newId) return false;
  const supplierList = budget.supplierList || [];
  const supplierIdx =
    payload.supplierListIndex ??
    supplierList.findIndex(
      (el) =>
        el.supplierId === payload.currentId &&
        el.calcOption === payload.calcOption
    );
  const renamesServices = !supplierList.some(
    (el, idx) =>
      idx !== supplierIdx &&
      el.supplierId === payload.currentId &&
      el.calcOption === payload.calcOption
  );
  return (
    renamesServices &&
    isStageScopedSupplierCardSwap(budget as Budget, payload, versionInfo)
  );
}

/** Categorias do card: as cadastradas nele e as dos serviços que ele mostra na etapa. */
function stageCardCategoryTypes(
  budget: Budget,
  payload: SupplierRenamePayload,
  versionInfo: versionInfo
): BudgetItemType[] {
  const types = new Set<BudgetItemType>();
  (budget.addedItemTypeList || []).forEach((item) => {
    if (
      item.supplierId === payload.currentId &&
      item.calcOption === payload.calcOption
    ) {
      types.add(item.type as BudgetItemType);
    }
  });
  (budget.budgetServices || []).forEach((service) => {
    if (
      service.supplierId === payload.currentId &&
      resolveServiceCalcOption(service, versionInfo) === payload.calcOption
    ) {
      types.add(service.type as BudgetItemType);
    }
  });
  return Array.from(types);
}

const cardSwapTransferPayload = (
  payload: SupplierRenamePayload,
  type: BudgetItemType,
  newName = ""
): TransferCategorySupplierPayload => ({
  type,
  calcOption: payload.calcOption as CalcOption,
  fromSupplierId: payload.currentId,
  toSupplierId: payload.newId,
  toSupplierName: newName,
});

function findStageScopedSupplierCardSwapBlockReason(
  budget: Budget,
  payload: SupplierRenamePayload,
  versionInfo: versionInfo
): string | null {
  for (const type of stageCardCategoryTypes(budget, payload, versionInfo)) {
    const alreadyInCard = (budget.addedItemTypeList || []).some(
      (item) =>
        item.supplierId === payload.newId &&
        item.type === type &&
        item.calcOption === payload.calcOption
    );
    if (alreadyInCard) {
      const label =
        BudgetItemTypeEnum[type as keyof typeof BudgetItemTypeEnum] ?? type;
      return `O fornecedor escolhido já tem a categoria ${label} na forma ${payload.calcOption}.`;
    }
    const reason = findTransferCategoryBlockReason(
      budget,
      cardSwapTransferPayload(payload, type),
      versionInfo
    );
    if (reason) return reason;
  }
  return null;
}

/**
 * Troca o fornecedor do card transferindo cada categoria dele (ver
 * `applyStageScopedTransferCategorySupplier`). O card continua no mesmo lugar
 * da lista, com as observações, só que com o fornecedor novo; o antigo só
 * permanece na forma se ainda tiver categoria nela.
 */
function applyStageScopedSupplierCardSwap(
  budget: Budget,
  payload: SupplierRenamePayload & { newName?: string },
  versionInfo: versionInfo,
  supplierIdx: number
): Budget {
  const types = stageCardCategoryTypes(budget, payload, versionInfo);
  let updatedBudget = budget;
  for (const type of types) {
    updatedBudget = applyStageScopedTransferCategorySupplier(
      updatedBudget,
      cardSwapTransferPayload(payload, type, payload.newName),
      versionInfo
    );
  }

  const card = (budget.supplierList || [])[supplierIdx];
  const isCardEntry =
    (supplierId: string) => (entry: Budget["supplierList"][number]) =>
      entry.supplierId === supplierId &&
      entry.calcOption === payload.calcOption;
  const oldStillHasCategory = (updatedBudget.addedItemTypeList || []).some(
    (item) =>
      item.supplierId === payload.currentId &&
      item.calcOption === payload.calcOption
  );
  const supplierList = (updatedBudget.supplierList || [])
    .map((entry, index) =>
      index === supplierIdx
        ? {
            supplierId: payload.newId,
            calcOption: payload.calcOption as CalcOption,
            ...(card?.descriptions ? { descriptions: card.descriptions } : {}),
          }
        : entry
    )
    .filter(
      (entry, index) =>
        index === supplierIdx || !isCardEntry(payload.newId)(entry)
    );
  if (
    oldStillHasCategory &&
    !supplierList.some(isCardEntry(payload.currentId))
  ) {
    supplierList.push({
      supplierId: payload.currentId,
      calcOption: payload.calcOption as CalcOption,
    });
  }

  return { ...updatedBudget, supplierList };
}

export type TransferServiceSupplierPayload = {
  /** `_id` da linha em `budgetServices`. */
  serviceId: string;
  type: BudgetItemType;
  typeName?: string;
  calcOption: "A" | "B" | "C" | "D";
  /** Id interno (pode vir sufixado) do fornecedor de origem. */
  fromSupplierId: string;
  /** Id interno JÁ resolvido para o destino (ver `resolveNewSupplierId`). */
  toSupplierId: string;
  toSupplierName: string;
  /** Etapa corrente — usada para reabilitar categoria/fornecedor soft-deletados. */
  version?: number;
};

/**
 * Transfere UM serviço para outro fornecedor, dentro da mesma forma de cobrança.
 *
 * A diferença essencial para `applyTransferCategorySupplier`: taxas, impostos e
 * câmbio NÃO pertencem ao serviço — pertencem à categoria (`baseValues` /
 * `mainAppliedTaxes`, indexados por `supplierId + category + calcOption +
 * version`). O cálculo sobrescreve o `taxData` do serviço com o da categoria,
 * preservando só a tarifa (ver `calculateAndMapService` em `calc-methods.ts`).
 *
 * Daí as duas regras que governam esta função:
 *
 * 1. Categoria de destino INEXISTENTE: é criada junto com um CLONE de
 *    `baseValues` e `mainAppliedTaxes` da origem, em todas as versões. Sem o
 *    clone o serviço cairia no `baseValue` zerado do fallback e depois seria
 *    reinicializado por `INIT_BASE_VALUES` com as taxas globais de Totais — o
 *    valor mudaria sozinho. Os `descriptions` da categoria (composição,
 *    política de cancelamento, forma de pagamento) NÃO são clonados: são texto
 *    negociado com o fornecedor de origem.
 * 2. Categoria de destino EXISTENTE: o perfil fiscal do destino NUNCA é
 *    sobrescrito — o serviço passa a ser calculado com o perfil que já está
 *    lá, e o valor pode mudar; é inerente ao modelo (uma categoria tem um
 *    único perfil fiscal por versão) e o modal de confirmação avisa
 *    explicitamente. O que É clonado da origem são só as VERSÕES que o
 *    destino nunca teve (mesmo filtro "não sobrescreve" de baixo, aplicado
 *    por versão em vez de por categoria inteira).
 *
 *    Sem isso, uma categoria de destino que só ganhou perfil em UMA versão
 *    (ex.: criada durante uma negociação) ficava com as outras versões sem
 *    `baseValues` para sempre: toda transferência seguinte via essa mesma
 *    categoria via `targetCategoryExists=true` e pulava o clone por inteiro,
 *    então a lacuna nunca era preenchida. Nessas versões sem perfil, o
 *    serviço cai no `baseValue` zerado do fallback em `calculateAndMapService`
 *    — Taxa MM/Imposto do serviço ficam 0 no agregado da categoria mesmo
 *    quando o serviço tem esses campos preenchidos (MMV-2511-X).
 *
 * `paymentSolicitation`, `files` e `itemTypeList` não são tocados: os dois
 * primeiros são da categoria (não do serviço) e o terceiro é indexado por forma
 * de cobrança, que aqui não muda. A origem nunca é podada, mesmo ficando vazia.
 */
export function applyTransferServiceSupplier(
  budget: Budget,
  payload: TransferServiceSupplierPayload
) {
  const {
    serviceId,
    type,
    calcOption,
    fromSupplierId,
    toSupplierId,
    toSupplierName,
  } = payload;

  if (
    !serviceId ||
    !fromSupplierId ||
    !toSupplierId ||
    fromSupplierId === toSupplierId
  ) {
    return budget;
  }

  const services = budget.budgetServices || [];
  const serviceIndex = services.findIndex(
    (el) =>
      el._id === serviceId &&
      el.supplierId === fromSupplierId &&
      el.type === type &&
      el.calcOption === calcOption
  );

  if (serviceIndex < 0) return budget;

  const currentVersion = payload.version ?? 0;

  const updatedBudget = {
    ...budget,
    supplierList: [...(budget.supplierList || [])],
    addedItemTypeList: [...(budget.addedItemTypeList || [])],
    budgetServices: [...services],
    baseValues: [...(budget.baseValues || [])],
    mainAppliedTaxes: [...(budget.mainAppliedTaxes || [])],
  };

  // `_id`, `versions`, `type`, `calcOption` e os três blocos de valores
  // (budgetValues / negotiationValue / closingValues) ficam intactos: só muda a
  // quem o serviço pertence.
  const movedService = {
    ...updatedBudget.budgetServices[serviceIndex],
    supplierId: toSupplierId,
    supplierName: toSupplierName,
  };

  // A ordem dos serviços dentro da categoria é a posição no array (ver
  // REORDER_SERVICES). Reposicionar no fim faz o serviço transferido aparecer
  // como o último da categoria de destino, em vez de numa posição arbitrária
  // herdada do índice antigo.
  updatedBudget.budgetServices.splice(serviceIndex, 1);
  updatedBudget.budgetServices.push(movedService);

  const withoutCurrentVersion = (versions: number[] | undefined) => {
    if (!versions?.length) return versions;
    const remaining = versions.filter((v) => v !== currentVersion);
    return remaining.length === versions.length ? versions : remaining;
  };

  const targetSupplierIndex = updatedBudget.supplierList.findIndex(
    (el) => el.supplierId === toSupplierId && el.calcOption === calcOption
  );

  if (targetSupplierIndex < 0) {
    updatedBudget.supplierList = [
      ...updatedBudget.supplierList,
      { supplierId: toSupplierId, calcOption, uid: uuid() },
    ];
  } else {
    // Fornecedor existente porém escondido da etapa: receber um serviço é uma
    // reintrodução explícita, então a exclusão da etapa é desfeita.
    const targetSupplier = updatedBudget.supplierList[targetSupplierIndex];
    const remaining = withoutCurrentVersion(targetSupplier.deletedInVersions);
    if (remaining !== targetSupplier.deletedInVersions) {
      updatedBudget.supplierList = updatedBudget.supplierList.map((el, index) =>
        index === targetSupplierIndex
          ? { ...el, deletedInVersions: remaining }
          : el
      );
    }
  }

  const targetCategoryIndex = updatedBudget.addedItemTypeList.findIndex(
    (el) =>
      el.supplierId === toSupplierId &&
      el.type === type &&
      el.calcOption === calcOption
  );
  const targetCategoryExists = targetCategoryIndex >= 0;

  if (!targetCategoryExists) {
    // Nasce sem `order`: o fornecedor de destino que nunca foi reordenado por
    // drag and drop mantém a ordem fixa de CATEGORY_PRIORITY_ORDER; se já foi,
    // a categoria nova entra no fim (ver `sortItemsByCategoryRespectingOrder`).
    updatedBudget.addedItemTypeList = [
      ...updatedBudget.addedItemTypeList,
      { type, supplierId: toSupplierId, calcOption },
    ];
  } else {
    const targetCategory = updatedBudget.addedItemTypeList[targetCategoryIndex];
    const remaining = withoutCurrentVersion(targetCategory.deletedInVersions);
    if (remaining !== targetCategory.deletedInVersions) {
      updatedBudget.addedItemTypeList = updatedBudget.addedItemTypeList.map(
        (el, index) =>
          index === targetCategoryIndex
            ? { ...el, deletedInVersions: remaining }
            : el
      );
    }
  }

  // Clona do perfil fiscal da ORIGEM só as VERSÕES que o destino ainda não
  // tem — nunca sobrescreve uma versão que o destino já possui (categoria
  // nova ou já existente: a regra é a mesma, por versão). Isso preenche tanto
  // a categoria nova (MMV-2511-X: antes só rodava com `!targetCategoryExists`,
  // então uma categoria de destino com perfil incompleto — ex.: criada numa
  // negociação, sem linha na versão do orçamento — nunca recebia a versão
  // que faltava em transferências seguintes).
  const originBaseValues = updatedBudget.baseValues.filter(
    (el) =>
      el.supplierId === fromSupplierId &&
      el.category === type &&
      el.calcOption === calcOption
  );

  const clonedBaseValues = originBaseValues
    .filter(
      (origin) =>
        !updatedBudget.baseValues.some(
          (el) =>
            el.supplierId === toSupplierId &&
            el.category === type &&
            el.calcOption === calcOption &&
            el.version === origin.version
        )
    )
    .map((origin) => ({
      ...origin,
      supplierId: toSupplierId,
      taxData: { ...origin.taxData },
      exchange: { ...origin.exchange },
      // Descritivos da categoria são do fornecedor de ORIGEM — a categoria
      // nova nasce com os campos em branco.
      descriptions: {
        composition: "",
        cancelPolicy: "",
        paymentMethod: "",
      },
    }));

  if (clonedBaseValues.length) {
    updatedBudget.baseValues = [
      ...updatedBudget.baseValues,
      ...clonedBaseValues,
    ];
  }

  const originAppliedTaxes = updatedBudget.mainAppliedTaxes.filter(
    (el) =>
      el.supplierId === fromSupplierId &&
      el.category === type &&
      el.calcOption === calcOption
  );

  const clonedAppliedTaxes = originAppliedTaxes
    .filter(
      (origin) =>
        !updatedBudget.mainAppliedTaxes.some(
          (el) =>
            el.supplierId === toSupplierId &&
            el.category === type &&
            el.calcOption === calcOption &&
            el.version === origin.version
        )
    )
    .map((origin) => ({
      ...origin,
      supplierId: toSupplierId,
      appliedTaxes: { ...origin.appliedTaxes },
    }));

  if (clonedAppliedTaxes.length) {
    updatedBudget.mainAppliedTaxes = [
      ...updatedBudget.mainAppliedTaxes,
      ...clonedAppliedTaxes,
    ];
  }

  return updatedBudget;
}

/** Evita que `isApiUpdate` permaneça após edições do usuário (re-disparava efeitos de load). */
function clearStaleApiUpdateFlag(
  budget: Budget,
  action: ActiveBudgetAction
): Budget {
  if (
    !budget.isApiUpdate ||
    action.type === "NEW_ACTIVE_BUDGET" ||
    action.type === "NO_ACTIVE_BUDGET" ||
    action.type === "UPDATE_FROM_API"
  ) {
    return budget;
  }
  return { ...budget, isApiUpdate: false };
}

/** Verifica se um TaxData possui ao menos uma taxa com valor parametrizado (> 0). */
function hasAnyNonZeroTax(taxData: Record<string, any> | undefined): boolean {
  if (!taxData) return false;
  return Object.values(taxData).some(
    (taxItem: any) =>
      taxItem &&
      typeof taxItem === "object" &&
      "value" in taxItem &&
      taxItem.value > 0
  );
}

/**
 * Garante que toda linha de `supplierList` tenha `uid`.
 *
 * Roda nas actions vindas da API (carga inicial / refresh), onde orçamentos
 * gravados antes do drag and drop chegam sem o campo. É backfill em memória:
 * não marca o orçamento como alterado (essas actions estão em
 * `API_DRIVEN_ACTION_TYPES`), e o `uid` só chega ao banco no próximo save.
 *
 * Sem isso, o `key` do React e o id do sortable cairiam de volta no índice —
 * exatamente o que faz a linha remontar (e perder o estado dos acordeões) a
 * cada reordenação.
 */
function withSupplierUids(budget: Budget): Budget {
  if (!budget || !Array.isArray(budget.supplierList)) return budget;
  if (budget.supplierList.every((el) => !!el?.uid)) return budget;

  return {
    ...budget,
    supplierList: budget.supplierList.map((el) =>
      el?.uid ? el : { ...el, uid: uuid() }
    ),
  };
}

export function activeBudgetReducer(
  state: Budget,
  action: ActiveBudgetAction
): Budget {
  let updatedBudget = { ...state };
  switch (action.type) {
    case "CHANGE_CALC_TYPE":
      updatedBudget.itemTypeList = {
        ...updatedBudget.itemTypeList,
        [action.payload.key]: action.payload.value,
      };
      toast.success("Tipo de calculo mudado para:" + action.payload.value);
      break;
    case "NO_ACTIVE_BUDGET":
      updatedBudget = {} as Budget;
      break;
    case "NEW_ACTIVE_BUDGET":
      updatedBudget = withSupplierUids(action.payload);
      break;
    case "ADD_SUPPLIER": {
      // A linha de fornecedor não conhece agrupamento: quem pertence a um
      // agrupamento é a LINHA (categoria + fornecedor), criada por
      // `ADD_ROW_TO_GROUP`.
      //
      // Se já existe um slot vazio neste bloco, reusa — o "Novo fornecedor"
      // intermitente deixava a linha anterior na lista (vazia ou oculta na
      // tela) e o próximo clique virava nº 11 em vez de nº 10.
      const calcOption = action.payload.calcOption;
      const currentList = updatedBudget.supplierList || [];
      const reusableEmpty = currentList.find(
        (el) =>
          el.calcOption === calcOption &&
          !el.supplierId &&
          !(el.deletedInVersions ?? []).length
      );
      if (reusableEmpty) break;

      updatedBudget.supplierList = [
        ...currentList,
        {
          supplierId: "",
          calcOption,
          uid: uuid(),
        },
      ];
      break;
    }
    case "REORDER_SUPPLIERS": {
      // `oldIndex`/`newIndex` são índices ABSOLUTOS em `supplierList`. A tela
      // mostra uma sublista (filtrada por calcOption + visibilidade de etapa),
      // então a conversão "posição visível → posição real" acontece na UI,
      // antes do dispatch.
      const { oldIndex, newIndex } = action.payload;
      const reorderedSuppliers = [...(updatedBudget.supplierList || [])];

      if (
        typeof oldIndex !== "number" ||
        typeof newIndex !== "number" ||
        oldIndex === newIndex ||
        oldIndex < 0 ||
        newIndex < 0 ||
        oldIndex >= reorderedSuppliers.length ||
        newIndex >= reorderedSuppliers.length
      ) {
        break;
      }

      const [movedSupplier] = reorderedSuppliers.splice(oldIndex, 1);
      reorderedSuppliers.splice(newIndex, 0, movedSupplier);
      updatedBudget.supplierList = reorderedSuppliers;
      break;
    }
    case "REORDER_CATEGORIES": {
      // `addedItemTypeList` é uma lista PLANA com as categorias de todos os
      // fornecedores. Em vez de mexer em posições relativas dentro dela,
      // reescrevemos `order` apenas nos itens do par (supplierId, calcOption)
      // afetado. Reescrever o bloco inteiro a cada drop (em vez de incrementar)
      // é o que mantém a ordem correta em reordenações consecutivas.
      const { supplierId, calcOption, orderedTypes } = action.payload;

      if (!Array.isArray(orderedTypes) || orderedTypes.length === 0) break;

      const positionByType = new Map<string, number>(
        orderedTypes.map((type: BudgetItemType, position: number) => [
          type,
          position,
        ])
      );

      updatedBudget.addedItemTypeList = (
        updatedBudget.addedItemTypeList || []
      ).map((el) => {
        if (el.supplierId !== supplierId || el.calcOption !== calcOption) {
          return el;
        }
        const position = positionByType.get(el.type);
        return position === undefined ? el : { ...el, order: position };
      });
      break;
    }
    // ---------------------------------------------------------------------
    // Agrupamento ("Dia 1", "Pré-evento", "Day Use")
    //
    // O agrupamento separa SERVIÇOS. O modelo tem três peças (ver
    // utils/budget-groups.ts):
    //
    //   budgetGroups        → quais agrupamentos existem e em que ordem
    //   AddedTypes.groupId  → a que agrupamento cada LINHA pertence
    //   ServiceData.groupId → a que LINHA cada serviço pertence
    //
    // Nenhuma destas actions altera valor, imposto ou total: o agrupamento é
    // camada de organização. Se algum valor mudar por causa delas, é bug — o
    // que muda é o PARTICIONAMENTO dos serviços, nunca o conteúdo deles.
    //
    // Diferente do desenho anterior, `supplierList` NÃO é mais reescrita: ela
    // continua sendo a ordem da visão "Por Fornecedor", que é uma partição
    // independente da do agrupamento.
    // ---------------------------------------------------------------------
    case "CREATE_GROUP": {
      const { name, calcOption } = action.payload;
      const trimmedName = String(name ?? "").trim();
      // Nome vazio não cria. Nome REPETIDO cria normalmente: a identidade é o
      // `id`, e bloquear "Dia 1" duas vezes só atrapalharia quem tem motivo.
      if (!trimmedName || !calcOption) break;

      updatedBudget.budgetGroups = [
        ...(updatedBudget.budgetGroups || []),
        { id: uuid(), name: trimmedName, calcOption },
      ];
      break;
    }
    /**
     * Traz para ESTE orçamento os agrupamentos da planilha padrão do cliente.
     *
     * Só ADICIONA. Nunca renomeia nem remove: o orçamento pode ter agrupamentos
     * próprios, e o template é uma sugestão do cliente, não a verdade sobre o
     * que já foi montado aqui.
     *
     * A identidade é o par (nome normalizado, forma de cobrança) — o mesmo par
     * que a geração da planilha usa para casar o serviço com o seu bloco. Um
     * "5 - LOJAS" que já exista em A não impede o de B, que é outro bloco na
     * tela; e agrupamento excluído numa etapa versionada continua existindo em
     * `budgetGroups`, então também conta como presente.
     */
    case "ADD_GROUPS_FROM_TEMPLATE": {
      const { groups } = action.payload as {
        groups?: Array<{ name?: string; calcOption?: string }>;
      };
      if (!Array.isArray(groups) || groups.length === 0) break;

      const current = updatedBudget.budgetGroups || [];
      const key = (name: string, calcOption: string) =>
        `${normalizeGroupName(name)}::${calcOption}`;
      const existing = new Set(
        current.map((el) => key(String(el.name ?? ""), String(el.calcOption)))
      );

      const created: typeof current = [];
      groups.forEach((group) => {
        const name = String(group?.name ?? "").trim();
        const calcOption = group?.calcOption;
        if (!name || !calcOption) return;

        const groupKey = key(name, calcOption);
        if (existing.has(groupKey)) return;
        existing.add(groupKey);

        created.push({ id: uuid(), name, calcOption } as (typeof current)[0]);
      });

      if (created.length === 0) break;
      updatedBudget.budgetGroups = [...current, ...created];
      break;
    }
    case "RENAME_GROUP": {
      const { groupId, name } = action.payload;
      const trimmedName = String(name ?? "").trim();
      if (!groupId || !trimmedName) break;

      updatedBudget.budgetGroups = (updatedBudget.budgetGroups || []).map(
        (el) => (el.id === groupId ? { ...el, name: trimmedName } : el)
      );
      break;
    }
    case "REORDER_GROUPS": {
      // `oldIndex`/`newIndex` são índices ABSOLUTOS em `budgetGroups` — mesmo
      // contrato de `REORDER_SUPPLIERS`. A lista exibida é filtrada por
      // calcOption e por etapa, então a conversão "posição visível → posição
      // real" acontece na UI, antes do dispatch.
      const { oldIndex, newIndex } = action.payload;
      const reorderedGroups = [...(updatedBudget.budgetGroups || [])];

      if (
        typeof oldIndex !== "number" ||
        typeof newIndex !== "number" ||
        oldIndex === newIndex ||
        oldIndex < 0 ||
        newIndex < 0 ||
        oldIndex >= reorderedGroups.length ||
        newIndex >= reorderedGroups.length
      ) {
        break;
      }

      const [movedGroup] = reorderedGroups.splice(oldIndex, 1);
      reorderedGroups.splice(newIndex, 0, movedGroup);
      updatedBudget.budgetGroups = reorderedGroups;
      break;
    }
    case "DELETE_GROUP": {
      const { groupId } = action.payload;
      const group = (updatedBudget.budgetGroups || []).find(
        (el) => el.id === groupId
      );
      if (!group) break;

      const groupVersion = action.versionInfo?.version ?? 0;
      const isVersionedGroupStage =
        groupVersion > 0 &&
        (action.versionInfo?.status === "NEGOTIATE" ||
          action.versionInfo?.status === "APPROVED" ||
          action.versionInfo?.status === "APROVED");

      // Excluir o agrupamento NUNCA exclui linha nem serviço. O conteúdo volta
      // para "Sem agrupamento" — um menu de organização visual não pode apagar
      // dado financeiro (ver §5.2 da documentação).
      if (isVersionedGroupStage) {
        // `budgetGroups` não é versionado: é a mesma lista lida por Orçamento,
        // Negociação e Fechamento. Remover aqui apagaria o agrupamento também
        // do orçamento base — mesmo contrato de `DELETE_SUPPLIER`.
        updatedBudget.budgetGroups = (updatedBudget.budgetGroups || []).map(
          (el) =>
            el.id === groupId
              ? {
                  ...el,
                  deletedInVersions: Array.from(
                    new Set([...(el.deletedInVersions ?? []), groupVersion])
                  ),
                }
              : el
        );
        // Linhas e serviços mantêm o `groupId`: na etapa versionada o
        // agrupamento fica invisível e eles caem em "Sem agrupamento" pelo
        // caminho do `groupId` órfão, sem perder o vínculo no Orçamento base.
        break;
      }

      updatedBudget.budgetGroups = (updatedBudget.budgetGroups || []).filter(
        (el) => el.id !== groupId
      );
      // Só os SERVIÇOS carregam o vínculo. `addedItemTypeList` não conhece
      // agrupamento — nada a limpar lá, e nenhuma duplicata a desfazer.
      updatedBudget.budgetServices = (updatedBudget.budgetServices || []).map(
        (el) => (el.groupId === groupId ? { ...el, groupId: undefined } : el)
      );
      break;
    }
    case "DUPLICATE_GROUP": {
      const { groupId } = action.payload;
      const groups = [...(updatedBudget.budgetGroups || [])];
      const sourceIndex = groups.findIndex((el) => el.id === groupId);
      if (sourceIndex === -1) break;

      const source = groups[sourceIndex];
      const duplicatedGroup = {
        id: uuid(),
        name: `${source.name} (Cópia)`,
        calcOption: source.calcOption,
      };
      groups.splice(sourceIndex + 1, 0, duplicatedGroup);
      updatedBudget.budgetGroups = groups;

      // Duplicar leva os SERVIÇOS junto — é o que "o Dia 2 é igual ao Dia 1"
      // significa, e sem eles a cópia nasceria vazia (a linha é derivada dos
      // serviços, não existe registro de linha para copiar sozinho).
      //
      // A cópia reusa o MESMO fornecedor e a MESMA categoria: nada é criado em
      // `supplierList` nem em `addedItemTypeList`, então não há duplicata para
      // a proteção de fornecedor repetido reagir.
      const copies = (updatedBudget.budgetServices || [])
        .filter(
          (el) =>
            el.calcOption === source.calcOption && el.groupId === source.id
        )
        .map((el) => ({ ...el, _id: uuid(), groupId: duplicatedGroup.id }));

      updatedBudget.budgetServices = [
        ...(updatedBudget.budgetServices || []),
        ...copies,
      ];
      break;
    }
    case "ADD_ROW_TO_GROUP": {
      // O formulário "Novo fornecedor em <agrupamento>": escolhe fornecedor e
      // categoria, e a linha nasce com o PRIMEIRO SERVIÇO dentro dela — os
      // mesmos dois passos do `addOption` da visão plana (ADD_BUDGET_ITEM +
      // ADD_SERVICE).
      //
      // É por isso que não existe registro de linha em lugar nenhum: a linha é
      // derivada dos serviços, e criar a linha é criar o serviço dela. Antes
      // havia uma lista `rows` no agrupamento só para segurar a linha vazia, e
      // ela dessincronizava — categoria de fornecedor já excluído continuava
      // aparecendo no agrupamento.
      const {
        supplierId: pickedSupplierId,
        type,
        calcOption,
        groupId,
      } = action.payload;
      if (!pickedSupplierId || !type || !calcOption) break;

      const targetGroupId =
        groupId && groupId !== UNGROUPED_ID ? groupId : undefined;

      // O fornecedor escolhido REUSA a linha que já existe na forma de cobrança.
      //
      // Este é o ponto do retorno do QA: "é o mesmo fornecedor… ele sai criando
      // aqui dois, deveria estar juntando tudo no mesmo". Aqui não se está
      // criando um fornecedor — está se dizendo em que dia um serviço dele
      // acontece. Duas linhas para o mesmo fornecedor é o que a visão plana
      // acusa como "Fornecedor duplicado na mesma forma de pagamento".
      //
      // A comparação é pelo id REAL: o id gravado pode estar sufixado
      // (`<id>|dup|N`, `<id>-A-2`), e é o id gravado — não o limpo — que precisa
      // ir para o serviço, senão ele não casa com nenhuma linha da tela.
      const pickedRealId = parseSupplierId(pickedSupplierId);
      const existingSupplierRow = (updatedBudget.supplierList || []).find(
        (el) =>
          el.calcOption === calcOption &&
          parseSupplierId(el.supplierId) === pickedRealId
      );
      const supplierId = existingSupplierRow?.supplierId ?? pickedSupplierId;

      if (!existingSupplierRow) {
        updatedBudget.supplierList = [
          ...(updatedBudget.supplierList || []),
          { supplierId, calcOption, uid: uuid() },
        ];
      }

      // A linha já existe neste agrupamento? A pergunta se responde nos
      // próprios serviços — não há declaração para consultar. Mesmo aviso do
      // `addOption` da visão plana, e pelo mesmo motivo: o formulário é "novo
      // fornecedor", não "mais um serviço no que já está aí".
      const rowAlreadyExists = (updatedBudget.budgetServices || []).some(
        (el) =>
          el.type === type &&
          el.supplierId === supplierId &&
          el.calcOption === calcOption &&
          (el.groupId ?? undefined) === targetGroupId
      );
      if (rowAlreadyExists) {
        toast.warn(
          "Essa categoria já existe para o fornecedor neste agrupamento"
        );
        return state;
      }

      // A categoria é do PAR, não do agrupamento: uma entrada só, criada apenas
      // se ainda não existir. O mesmo fornecedor com a mesma categoria em dois
      // dias continua sendo UMA categoria na visão plana, com os serviços dos
      // dois dentro — "deveria acrescentar a categoria nele… incluiria mais uma
      // linha de serviço", no retorno do QA.
      const hasCategory = (updatedBudget.addedItemTypeList || []).some(
        (el) =>
          el.type === type &&
          el.supplierId === supplierId &&
          el.calcOption === calcOption
      );
      if (!hasCategory) {
        updatedBudget.addedItemTypeList = [
          ...(updatedBudget.addedItemTypeList || []),
          { type, supplierId, calcOption },
        ];
      }

      return activeBudgetReducer(updatedBudget, {
        ...action,
        type: "ADD_SERVICE",
        payload: {
          supplierId,
          supplierName: action.payload.supplierName || "",
          type,
          calcOption,
          groupId: targetGroupId,
          tabActive: action.payload.tabActive,
          versionInfo: action.versionInfo ?? { version: 0, status: "OPEN" },
          projectEvent: action.payload.projectEvent,
        },
      });
    }
    case "REMOVE_ROW_FROM_GROUP": {
      // O ✕ da linha na visão agrupada remove SÓ o que está naquele
      // agrupamento — os serviços daquela linha.
      //
      // Antes ele chamava `DELETE_BUDGET_ITEM`, que apaga a categoria do PAR
      // (fornecedor + categoria) inteira: remover a Hospedagem do "Pré-evento"
      // levava junto a Hospedagem de todos os outros dias. Retorno do QA: "eu
      // ia remover só o do Pré-evento, ele acabou removendo tudo".
      //
      // A categoria só é removida de fato quando o par fica sem serviço NENHUM
      // na forma de cobrança — aí manter uma categoria vazia que o usuário
      // acabou de mandar remover seria só um resto na tela.
      const { type, supplierId, calcOption, groupId } = action.payload;
      if (!type || !supplierId || !calcOption) break;

      const fromGroupId =
        groupId && groupId !== UNGROUPED_ID ? groupId : undefined;

      const version = action.versionInfo?.version ?? 0;
      const isVersionedStage =
        version > 0 &&
        (action.versionInfo?.status === "NEGOTIATE" ||
          action.versionInfo?.status === "APPROVED" ||
          action.versionInfo?.status === "APROVED");

      const pertenceALinha = (el: ServiceData) =>
        el.type === type &&
        el.supplierId === supplierId &&
        el.calcOption === calcOption &&
        (el.groupId ?? undefined) === fromGroupId;

      if (isVersionedStage) {
        // Etapa versionada não apaga: tira a versão atual do serviço, como
        // `DELETE_SERVICE` e `DELETE_BUDGET_ITEM` fazem. O orçamento base
        // continua intacto.
        updatedBudget.budgetServices = updatedBudget.budgetServices.map((el) =>
          pertenceALinha(el)
            ? {
                ...el,
                versions: (el.versions ?? []).filter((v) => v !== version),
              }
            : el
        );
        break;
      }

      updatedBudget.budgetServices = updatedBudget.budgetServices.filter(
        (el) => !pertenceALinha(el)
      );

      const parAindaTemServico = updatedBudget.budgetServices.some(
        (el) =>
          el.type === type &&
          el.supplierId === supplierId &&
          el.calcOption === calcOption
      );

      if (!parAindaTemServico) {
        updatedBudget.addedItemTypeList =
          updatedBudget.addedItemTypeList.filter(
            (el) =>
              !(
                el.type === type &&
                el.supplierId === supplierId &&
                el.calcOption === calcOption
              )
          );
        // `?? []`: orçamento legado pode não ter a lista, e aqui já estamos
        // num caminho de limpeza — não é lugar de estourar.
        updatedBudget.baseValues = (updatedBudget.baseValues ?? []).filter(
          (el) =>
            !(
              el.supplierId === supplierId &&
              el.category === type &&
              el.calcOption === calcOption &&
              el.version === version
            )
        );
      }

      break;
    }
    case "MOVE_ROW_TO_GROUP": {
      // Move a LINHA e os serviços dela. Identificada pelo par + agrupamento de
      // origem: dentro de uma forma de cobrança isso é único.
      const { type, supplierId, calcOption, fromGroupId, targetGroupId } =
        action.payload;
      if (!type || !supplierId || !calcOption) break;

      const from =
        fromGroupId && fromGroupId !== UNGROUPED_ID ? fromGroupId : undefined;
      const to =
        targetGroupId && targetGroupId !== UNGROUPED_ID
          ? targetGroupId
          : undefined;
      if (from === to) break;

      // Só os SERVIÇOS mudam de vínculo. `addedItemTypeList` não é tocada: a
      // categoria continua sendo do par, e mover a linha de dia não muda isso.
      updatedBudget.budgetServices = (updatedBudget.budgetServices || []).map(
        (el) =>
          el.type === type &&
          el.supplierId === supplierId &&
          el.calcOption === calcOption &&
          (el.groupId ?? undefined) === from
            ? { ...el, groupId: to }
            : el
      );
      // `budgetGroups` não é tocada: ela só diz quais agrupamentos existem. A
      // linha some da origem e aparece no destino porque é derivada do
      // `groupId` dos serviços — não há declaração para manter em sincronia.
      break;
    }
    case "SET_NOTE_ADVANCE": {
      const incoming = action.payload.noteAdvance as NoteAdvance;
      const existing = updatedBudget.noteAdvances || [];
      const found = existing.some((el) => el.version === incoming.version);
      updatedBudget.noteAdvances = found
        ? existing.map((el) =>
            el.version === incoming.version ? incoming : el
          )
        : [...existing, incoming];
      break;
    }
    case "NEW_GLOBAL_TAX_DATA":
      const oldTaxData = updatedBudget.budgetWideAppliedTaxes.find(
        (el) => el.version === action.payload.newBudgetWideAppliedTaxes.version
      );
      if (!oldTaxData) {
        updatedBudget.budgetWideAppliedTaxes.push(
          action.payload.newBudgetWideAppliedTaxes
        );
      } else {
        updatedBudget.budgetWideAppliedTaxes =
          updatedBudget.budgetWideAppliedTaxes.map((el) => {
            if (
              el.version === action.payload.newBudgetWideAppliedTaxes.version
            ) {
              return action.payload.newBudgetWideAppliedTaxes;
            }
            return el;
          });
      }

      const applyExchange = shouldApplyExchange(
        action.payload.newBudgetWideAppliedTaxes.exchange,
        oldTaxData?.exchange
      );

      updatedBudget.baseValues = updatedBudget.baseValues.map((el) => {
        if (el.version === action.payload.newBudgetWideAppliedTaxes.version) {
          const newTaxData = action.payload.newBudgetWideAppliedTaxes.taxData;
          const previousGlobalTaxData = oldTaxData?.taxData;
          const updatedTaxData = { ...el.taxData };

          // Propaga o valor da taxa de Totais sempre que ele MUDOU em relação
          // ao valor global anterior — inclusive quando foi zerado (ex.: 10 -> 0).
          // Assim "zerar uma taxa em Totais" reflete em todas as categorias, sem
          // sobrescrever campos que o usuário não alterou nesta ação (ex.: só câmbio).
          Object.keys(newTaxData).forEach((key) => {
            const taxItem = newTaxData[key];
            if (
              !taxItem ||
              typeof taxItem !== "object" ||
              !("value" in taxItem)
            )
              return;

            const previousItem = previousGlobalTaxData?.[key as keyof TaxData];
            const previousValue =
              previousItem &&
              typeof previousItem === "object" &&
              "value" in previousItem
                ? previousItem.value
                : 0;

            if (taxItem.value === previousValue) return;

            // Taxa customizada na categoria (diferente do que Totais tinha
            // antes) é preservada; as demais taxas da categoria seguem Totais.
            // Ex.: categoria com Taxa de Serviço 5% continua com 5% quando
            // Totais aplica 10%, mas recebe o Imposto aplicado em Totais.
            const categoryItem = el.taxData?.[key as keyof TaxData];
            const categoryValue =
              categoryItem &&
              typeof categoryItem === "object" &&
              "value" in categoryItem
                ? categoryItem.value || 0
                : 0;
            const isCustomizedInCategory =
              categoryValue !== (previousValue || 0) ||
              (categoryValue !== 0 &&
                (categoryItem as any)?.typeTax !==
                  (previousItem as any)?.typeTax);
            if (isCustomizedInCategory) return;

            updatedTaxData[key as keyof TaxData] = taxItem;
          });

          el.taxData = updatedTaxData;
          // O câmbio segue o comportamento anterior: categoria com taxas
          // customizadas à mão não recebe o câmbio de Totais.
          if (applyExchange && !(el as any).taxDataOverride)
            el.exchange = action.payload.newBudgetWideAppliedTaxes.exchange;
        }
        return el;
      });

      break;
    case "UPDATE_SUPPLIER_LIST":
      const supplierIdx =
        action.payload.supplierListIndex != null
          ? action.payload.supplierListIndex
          : updatedBudget.supplierList.findIndex(
              (el) =>
                el.supplierId === action.payload.currentId &&
                el.calcOption === action.payload.calcOption
            );
      const supplierRename: SupplierRenamePayload = {
        currentId: action.payload.currentId,
        newId: action.payload.newId,
        calcOption: action.payload.calcOption,
      };
      // Os serviços só trocam de fornecedor quando nenhum outro card usa o id
      // antigo nesta forma (ver `anotherEntryStillUsesOldId` abaixo).
      const renamesServices = !updatedBudget.supplierList.some(
        (el, idx) =>
          idx !== supplierIdx &&
          el.supplierId === supplierRename.currentId &&
          el.calcOption === supplierRename.calcOption
      );
      const renameBlockReason = getSupplierRenameBlockReason(
        updatedBudget,
        { ...supplierRename, supplierListIndex: supplierIdx },
        action.versionInfo
      );
      if (renameBlockReason) {
        toast.error(renameBlockReason);
        break;
      }
      if (
        renamesServices &&
        isStageScopedSupplierCardSwap(
          updatedBudget,
          supplierRename,
          action.versionInfo
        )
      ) {
        updatedBudget = applyStageScopedSupplierCardSwap(
          updatedBudget,
          { ...supplierRename, newName: action.payload.newName },
          action.versionInfo!,
          supplierIdx
        );
        if (!action.payload.silent) {
          toast.success("Lista de fornecedores atualizada.");
        }
        break;
      }
      // Calculado antes da troca: depois dela os serviços já têm o id novo.
      const stageMoveDestinationsBeforeRename = renamesServices
        ? stageMoveDestinations(updatedBudget, supplierRename)
        : [];
      updatedBudget.supplierList = updatedBudget.supplierList.map(
        (supplier, index) =>
          index === supplierIdx
            ? {
                // Espalha a linha inteira: `uid` é a `key` do React / id do
                // sortable, e `deletedInVersions` é o que esconde o card numa
                // etapa. Reconstruir do zero descartava os dois — ao escolher
                // o fornecedor a linha remontava (o campo preenchido "sumia")
                // e uma exclusão de etapa era desfeita.
                //
                // O agrupamento não aparece aqui: quem pertence a um
                // agrupamento é a LINHA (categoria + fornecedor), em
                // `addedItemTypeList`, não a linha de fornecedor.
                ...supplier,
                supplierId: action.payload.newId,
                calcOption: action.payload.calcOption,
              }
            : supplier
      );

      const anotherEntryStillUsesOldId = updatedBudget.supplierList.some(
        (el, idx) =>
          idx !== supplierIdx &&
          el.supplierId === action.payload.currentId &&
          el.calcOption === action.payload.calcOption
      );

      if (!anotherEntryStillUsesOldId) {
        updatedBudget.budgetServices = updatedBudget.budgetServices.map(
          (service) => {
            if (
              service.supplierId === action.payload.currentId &&
              service.calcOption === action.payload.calcOption
            ) {
              service.supplierId = action.payload.newId;
              service.supplierName = action.payload.newName;
            }
            return service;
          }
        );
        updatedBudget.addedItemTypeList = updatedBudget.addedItemTypeList.map(
          (item) => {
            if (
              item.supplierId === action.payload.currentId &&
              item.calcOption === action.payload.calcOption
            ) {
              item.supplierId = action.payload.newId;
            }
            return item;
          }
        );
        updatedBudget.baseValues = updatedBudget.baseValues.map((el) => {
          if (
            el.supplierId === action.payload.currentId &&
            el.calcOption === action.payload.calcOption
          ) {
            el.supplierId = action.payload.newId;
          }
          return el;
        });
        updatedBudget.paymentSolicitation =
          updatedBudget.paymentSolicitation.map((paymentInfo) => {
            if (
              paymentInfo.supplierId === action.payload.currentId &&
              paymentInfo.calcOption === action.payload.calcOption
            ) {
              return {
                ...paymentInfo,
                supplierId: action.payload.newId,
              };
            }
            return paymentInfo;
          });
        updatedBudget = carryStageMoveDestinationsOnSupplierRename(
          updatedBudget,
          supplierRename,
          stageMoveDestinationsBeforeRename
        );
        updatedBudget = renameSupplierInAllTotalsTrees(
          updatedBudget,
          supplierRename,
          stageMoveDestinationsBeforeRename
        );
      }

      updatedBudget.mainAppliedTaxes = updatedBudget.mainAppliedTaxes.map(
        (appliedTax) => {
          if (
            appliedTax.supplierId === action.payload.currentId &&
            appliedTax.calcOption === action.payload.calcOption
          ) {
            return {
              ...appliedTax,
              supplierId: action.payload.newId,
            };
          }

          return appliedTax;
        }
      );

      if (!action.payload.silent) {
        toast.success("Lista de fornecedores atualizada.");
      }
      break;
    case "DELETE_SUPPLIER": {
      const deleteSlotIdx =
        action.payload.supplierListIndex != null
          ? action.payload.supplierListIndex
          : updatedBudget.supplierList.findIndex(
              (el) =>
                el.supplierId === action.payload.supplierId &&
                el.calcOption === action.payload.calcOption
            );

      // MMV-2419-9: em qualquer etapa versionada (negociação OU fechamento, version > 0) a
      // exclusão é "soft" e usa O MESMO mecanismo: remove apenas a versão atual de
      // service.versions, preservando o serviço no orçamento base. O export/comparativo lê
      // os serviços de cada coluna via `allServices`, que é filtrado por `versions` — então o
      // item some da coluna da etapa (aparece zerado + savings) e permanece na coluna base.
      // (Nulificar closingValues NÃO funcionava: o serviço seguia em `versions`/allServices e
      //  era renderizado com o valor base, sem savings.)
      const currentVersion = action.versionInfo?.version ?? 0;
      const isVersionedStage =
        currentVersion > 0 &&
        (action.versionInfo?.status === "NEGOTIATE" ||
          action.versionInfo?.status === "APPROVED" ||
          action.versionInfo?.status === "APROVED");

      // Calculado ANTES de qualquer remoção, ignorando o slot alvo — assim o
      // resultado é o mesmo tanto no caminho que remove da lista quanto no que
      // preserva a lista (etapa versionada).
      const anotherEntryKeepsId = updatedBudget.supplierList.some(
        (el, i) =>
          i !== deleteSlotIdx &&
          el.supplierId === action.payload.supplierId &&
          el.calcOption === action.payload.calcOption
      );

      // `supplierList` NÃO é versionado: é a MESMA lista lida por Orçamento,
      // Negociação e Fechamento. Removê-la numa etapa versionada apagava o card do
      // fornecedor também do orçamento original, deixando os serviços órfãos —
      // invisíveis na tela, mas ainda somados nos totais e ainda impressos no export
      // ("blocos e valores fantasmas"). Só o orçamento base (v0/OPEN) pode remover a
      // linha; nas etapas versionadas quem esconde o card é a renderização, que
      // deriva a visibilidade de `service.versions` (ver ActiveBudget/SupplierRows).
      //
      // Exceção: se outro slot da lista ainda usa o mesmo fornecedor+bloco, os
      // serviços pertencem também a esse outro slot e não podem ser escondidos por
      // versão. Aí a linha duplicada é removida normalmente — não há dado a perder.
      if ((!isVersionedStage || anotherEntryKeepsId) && deleteSlotIdx > -1) {
        updatedBudget.supplierList = updatedBudget.supplierList.filter(
          (_el, i) => i !== deleteSlotIdx
        );
      } else if (isVersionedStage && deleteSlotIdx > -1) {
        // A linha fica na lista, então a exclusão precisa ficar registrada NELA.
        // Sem isso, o fornecedor sem nenhum serviço (o "A DEFINIR", que não tem
        // `versions` de onde tirar a versão atual) continuava visível para sempre
        // e o delete era um no-op silencioso — o Swal "Apagado" aparecia, o
        // autosave rodava, e depois do F5 o card voltava igual.
        updatedBudget.supplierList = updatedBudget.supplierList.map((el, i) =>
          i === deleteSlotIdx
            ? {
                ...el,
                deletedInVersions: Array.from(
                  new Set([...(el.deletedInVersions ?? []), currentVersion])
                ),
              }
            : el
        );
      }

      if (!anotherEntryKeepsId) {
        if (isVersionedStage) {
          // MMV-2659: mesma regra do DELETE_BUDGET_ITEM — a forma da ETAPA
          // decide de qual card o serviço faz parte agora.
          updatedBudget.budgetServices = updatedBudget.budgetServices.map(
            (el) =>
              el.supplierId === action.payload.supplierId &&
              resolveServiceCalcOption(el, action.versionInfo) ===
                action.payload.calcOption
                ? {
                    ...el,
                    versions: (el.versions ?? []).filter(
                      (v) => v !== currentVersion
                    ),
                  }
                : el
          );
        } else {
          updatedBudget.addedItemTypeList =
            updatedBudget.addedItemTypeList.filter(
              (el) =>
                !(
                  el.supplierId === action.payload.supplierId &&
                  el.calcOption === action.payload.calcOption
                )
            );
          updatedBudget.budgetServices = updatedBudget.budgetServices.filter(
            (el) =>
              !(
                el.supplierId === action.payload.supplierId &&
                el.calcOption === action.payload.calcOption
              )
          );
          updatedBudget.baseValues = updatedBudget.baseValues.filter(
            (el) =>
              !(
                el.supplierId === action.payload.supplierId &&
                el.calcOption === action.payload.calcOption &&
                el.version === action.versionInfo?.version
              )
          );
          updatedBudget.mainAppliedTaxes = (
            updatedBudget.mainAppliedTaxes || []
          ).filter(
            (el) =>
              !(
                el.supplierId === action.payload.supplierId &&
                el.calcOption === action.payload.calcOption
              )
          );
          updatedBudget.paymentSolicitation = (
            updatedBudget.paymentSolicitation || []
          ).filter(
            (el) =>
              !(
                el.supplierId === action.payload.supplierId &&
                el.calcOption === action.payload.calcOption
              )
          );
        }
      }
      break;
    }
    case "ADD_BUDGET_ITEM": {
      if (state.addedItemTypeList?.includes(action.payload)) {
        toast.warn("Tipo já existente");
        return state;
      }

      // MMV-2643: na negociação/fechamento, excluir a categoria não a remove de
      // `addedItemTypeList` (a lista é de todas as etapas — ver
      // DELETE_BUDGET_ITEM), só a esconde da etapa. Incluí-la de novo reaproveita
      // a entrada: empurrar outra criaria duplicata da mesma identidade, e a
      // marca de exclusão da etapa precisa ser desfeita para a categoria voltar.
      const { supplierId, type, calcOption } = action.payload;
      const version = action.versionInfo?.version ?? 0;
      const existing = (state.addedItemTypeList || []).find(
        (el) =>
          el.supplierId === supplierId &&
          el.type === type &&
          el.calcOption === calcOption
      );

      if (!existing) {
        updatedBudget.addedItemTypeList = [
          ...(state.addedItemTypeList || []),
          action.payload,
        ];
        break;
      }

      updatedBudget.addedItemTypeList = state.addedItemTypeList.map((el) =>
        el === existing
          ? {
              ...el,
              deletedInVersions: withoutDeletedVersion(
                el.deletedInVersions,
                version
              ),
            }
          : el
      );
      break;
    }
    case "DELETE_SERVICE": {
      const version = action.versionInfo?.version ?? 0;
      const status = action.versionInfo?.status;

      if (version === 0 && status !== "APPROVED") {
        // Orçamento original: remove o serviço inteiro
        updatedBudget.budgetServices = updatedBudget.budgetServices.filter(
          (el) => el._id !== action.payload
        );
      } else {
        // MMV-2419-9: negociação E fechamento (version > 0) removem a versão atual de
        // service.versions, para o serviço sumir da etapa (zerado + savings no comparativo)
        // e permanecer no orçamento original. Nulificar closingValues no fechamento NÃO
        // funcionava: o serviço seguia em `versions`/allServices e era renderizado com o
        // valor base (sem savings).
        updatedBudget.budgetServices = updatedBudget.budgetServices.map(
          (el) => {
            if (el._id !== action.payload) return el;
            return {
              ...el,
              versions: (el.versions ?? []).filter((v) => v !== version),
            };
          }
        );
      }
      toast.success("Serviço removido");
      break;
    }
    case "REPLACE_SERVICE_ID": {
      const { fromId, toId } = action.payload as {
        fromId: string;
        toId: string;
      };
      if (!fromId || !toId || fromId === toId) break;
      updatedBudget.budgetServices = updatedBudget.budgetServices.map((el) =>
        el._id === fromId ? { ...el, _id: toId } : el
      );
      break;
    }
    case "RESTORE_SERVICE": {
      const snapshot = action.payload as ServiceData;
      if (!snapshot?._id) break;
      const already = updatedBudget.budgetServices.some(
        (el) => el._id === snapshot._id
      );
      if (!already) {
        updatedBudget.budgetServices = [
          ...updatedBudget.budgetServices,
          snapshot,
        ];
      }
      break;
    }
    case "ADD_SERVICE":
      // Busca as taxas aplicadas da categoria (mainAppliedTaxes) se existirem
      // Se não existir configuração da categoria, usa o padrão do sistema (todas as taxas habilitadas)
      const categoryMainAppliedTaxes = state.mainAppliedTaxes?.find(
        (el) =>
          el.supplierId === action.payload.supplierId &&
          el.category === action.payload.type &&
          el.calcOption === action.payload.calcOption
      );

      // Define as appliedTaxes herdadas da categoria ou usa o padrão do sistema
      const inheritedAppliedTaxes = categoryMainAppliedTaxes?.appliedTaxes || {
        serviceTax: true,
        iss: true,
        cbs: true,
        ibs: true,
        tribute: true,
        supplier_tribute: true,
        localTax: true,
        tourismTax: true,
        fee: true,
        iof: true,
        markup: true,
        overhead: true,
        commission: true,
        incentive: true,
        doubleCharged: true,
      };

      // Obtém o câmbio do evento do projeto (se disponível no payload)
      const eventExchange = action.payload.projectEvent
        ? getEventExchange(action.payload.projectEvent)
        : null;

      // Busca os valores de taxa (taxData) já parametrizados para a categoria (herdados
      // de Totais na criação da categoria, ou customizados manualmente) para que os
      // serviços vinculados também nasçam com esses valores.
      const categoryBaseValue = state.baseValues?.find(
        (el) =>
          el.supplierId === action.payload.supplierId &&
          el.category === action.payload.type &&
          el.calcOption === action.payload.calcOption &&
          el.version === (action.payload.versionInfo?.version ?? 0)
      );
      const inheritedTaxData = categoryBaseValue?.taxData
        ? {
            ...initialServiceData.budgetValues.taxData,
            ...categoryBaseValue.taxData,
          }
        : { ...initialServiceData.budgetValues.taxData };

      // Cria os valores base do serviço
      const baseServiceValues = {
        ...initialServiceData.budgetValues,
        appliedTaxes: { ...inheritedAppliedTaxes },
        taxData: {
          ...inheritedTaxData,
          tariff: {
            ...inheritedTaxData.tariff,
            typeTax: "DAILY",
          },
        },
      };

      // Aplica o câmbio do evento aos valores do serviço
      const serviceValuesWithExchange = applyEventExchangeToService(
        baseServiceValues,
        eventExchange
      );

      const newService = {
        ...initialServiceData,
        type: action.payload.type,
        supplierId: action.payload.supplierId,
        supplierName: action.payload.supplierName,
        calcOption: action.payload.calcOption,
        // O serviço nasce dentro do agrupamento da LINHA que o criou. Sem isto
        // um serviço adicionado no "Dia 1" cairia em "Sem agrupamento" e a
        // linha ficaria vazia logo depois de o usuário preenchê-la. A visão
        // plana não manda `groupId` — lá a criação é do par, não da linha.
        groupId:
          action.payload.groupId && action.payload.groupId !== UNGROUPED_ID
            ? action.payload.groupId
            : undefined,
        _id: uuid(),
        budgetValues: serviceValuesWithExchange,
        negotiationValue: [] as ServiceValues[], // Inicializa como array vazio tipado
      };
      if (action.payload.versionInfo.version > 0) {
        newService.versions = [action.payload.versionInfo.version];
        for (let i = 0; i < action.payload.versionInfo.version; i++) {
          const negotiationBaseValues = {
            ...initialServiceData.budgetValues,
            appliedTaxes: { ...inheritedAppliedTaxes },
            taxData: {
              ...inheritedTaxData,
              tariff: {
                ...inheritedTaxData.tariff,
                typeTax: "DAILY",
              },
            },
          };

          // Aplica câmbio do evento também aos negotiationValue
          const negotiationValuesWithExchange = applyEventExchangeToService(
            negotiationBaseValues,
            eventExchange
          );

          newService.negotiationValue.push(negotiationValuesWithExchange);
        }
      }

      // If insertAtIndex is provided, insert at that position; otherwise, add at the end
      if (
        action.payload.insertAtIndex !== undefined &&
        action.payload.insertAtIndex >= 0
      ) {
        // Create a new array to trigger React re-render
        const newServices = [...updatedBudget.budgetServices];
        newServices.splice(action.payload.insertAtIndex, 0, newService);
        updatedBudget.budgetServices = newServices;
      } else {
        updatedBudget.budgetServices = [
          ...updatedBudget.budgetServices,
          newService,
        ];
      }

      // The "Serviço adicionado" toast is fired by `dispatchAddService` /
      // the ADD_ROW_TO_GROUP gate, once the insert is accepted. Toasting here
      // ran again whenever React replayed the reducer, with no extra row left
      // on screen.
      break;
    case "UPDATE_SERVICES_EXCHANGE":
      // Atualiza o câmbio de todos os serviços, respeitando overrides manuais
      if (
        updatedBudget.budgetServices &&
        updatedBudget.budgetServices.length > 0
      ) {
        // Obtém versionInfo do action ou usa padrão (v0 OPEN)
        const versionInfo = action.versionInfo || {
          version: 0,
          status: "OPEN",
          comparisonVersion: 0,
        };

        updatedBudget.budgetServices = updatedBudget.budgetServices.map(
          (service) => {
            const updatedService = { ...service };

            // Atualiza APENAS a versão ativa baseado no status
            if (versionInfo.status === "APPROVED") {
              // Atualiza apenas closingValues
              updatedService.closingValues = applyEventExchangeToService(
                service.closingValues,
                action.payload.eventExchange
              );
            } else if (
              versionInfo.version > 0 &&
              versionInfo.status === "NEGOTIATE"
            ) {
              // Atualiza apenas a versão de negociação ativa
              const negotiationIndex = versionInfo.version - 1;
              if (
                service.negotiationValue &&
                service.negotiationValue[negotiationIndex]
              ) {
                updatedService.negotiationValue = [...service.negotiationValue];
                updatedService.negotiationValue[negotiationIndex] =
                  applyEventExchangeToService(
                    service.negotiationValue[negotiationIndex],
                    action.payload.eventExchange
                  );
              }
            } else {
              // Atualiza apenas budgetValues (v0)
              updatedService.budgetValues = applyEventExchangeToService(
                service.budgetValues,
                action.payload.eventExchange
              );
            }

            return updatedService;
          }
        );

        toast.success("Câmbio atualizado em todos os serviços");
      }
      break;
    case "REORDER_SERVICES":
      const { oldIndex, newIndex } = action.payload;
      const reorderedServices = [...updatedBudget.budgetServices];
      const [movedService] = reorderedServices.splice(oldIndex, 1);
      reorderedServices.splice(newIndex, 0, movedService);
      updatedBudget.budgetServices = reorderedServices;
      toast.success("Serviço reordenado");
      break;
    case "MOVE_CATEGORY":
      updatedBudget = applyMoveCategory(
        updatedBudget,
        action.payload,
        action.versionInfo
      );
      break;
    case "TRANSFER_CATEGORY_SUPPLIER":
      updatedBudget = applyTransferCategorySupplier(
        updatedBudget,
        action.payload,
        action.versionInfo
      );
      break;
    case "TRANSFER_SERVICE_SUPPLIER":
      updatedBudget = applyTransferServiceSupplier(updatedBudget, {
        ...action.payload,
        version: action.payload?.version ?? action.versionInfo?.version ?? 0,
      });
      break;
    case "YES_NO_SWITCH_CHANGE":
      if (
        !!updatedBudget.itemTypeList &&
        !!updatedBudget.itemTypeList[
          action.payload.activeCalcOption as keyof ItemTypeListPerCalcOption
        ]
      ) {
        updatedBudget.itemTypeList[
          action.payload.activeCalcOption as keyof ItemTypeListPerCalcOption
        ][action.payload.type] = action.payload.newValue;
      } else if (
        !!updatedBudget.itemTypeList &&
        !updatedBudget.itemTypeList[
          action.payload.activeCalcOption as keyof ItemTypeListPerCalcOption
        ]
      ) {
        updatedBudget.itemTypeList = {
          ...updatedBudget.itemTypeList,
          [action.payload.activeCalcOption as keyof ItemTypeListPerCalcOption]:
            {
              [action.payload.type]: action.payload.newValue,
            },
        };
      } else {
        updatedBudget = {
          ...updatedBudget,
          itemTypeList: initialItemTypeList(),
        };
        updatedBudget.itemTypeList[
          action.payload.activeCalcOption as keyof ItemTypeListPerCalcOption
        ][action.payload.type] = action.payload.newValue;
      }
      toast.success("Lista de opções atualizada");
      break;
    case "UPDATE_SERVICE":
      const servicesList = updatedBudget.budgetServices.map((el) => {
        if (el._id === action.payload._id) {
          return action.payload;
        } else return el;
      });
      updatedBudget.budgetServices = servicesList;
      // UPDATE_SERVICE is an edit of an existing line. Toasting
      // "Serviço adicionado!" here raced with ADD_SERVICE when the user
      // left an unsaved field and clicked "+ Novo serviço": blur flushed
      // the edit (this action) and the click added a line, and the two
      // toasts stacked / retriggered as the list remounted.
      break;
    case "UPDATE_CALC_VALUES":
      const servicesListCalc = updatedBudget.budgetServices.map((el) => {
        if (el._id === action.payload._id) {
          return action.payload;
        } else return el;
      });
      updatedBudget.budgetServices = servicesListCalc;
      break;

    case "UPDATE_BASE_CALC_VALUES":
      const baseValueVersion =
        action.versionInfo?.version ?? action.payload?.version ?? 0;
      const newBaseValues = updatedBudget.baseValues.map((el) => {
        if (
          el.supplierId === action.payload.supplierId &&
          el.category === action.payload.type &&
          el.calcOption === action.payload.calcOption &&
          el.version === baseValueVersion
        ) {
          const updatedValue = {
            ...el,
            [action.payload?.field]: action.payload?.value,
          };
          // Se o campo alterado for exchange, verifica se deve marcar como override
          if (action.payload?.field === "exchange") {
            // Se o payload explicitamente define isOverride, usa esse valor
            // Isso permite que o resetToDefault force isOverride = false
            if (action.payload.value.hasOwnProperty("isOverride")) {
              updatedValue.isOverride = action.payload.value.isOverride;
            } else {
              // Caso contrário, marca como override pois foi editado manualmente
              updatedValue.isOverride = true;
            }
          }

          // Se o campo alterado for taxData, marca como customizado manualmente
          // para que futuras alterações de taxas em Totais não sobrescrevam esta categoria
          if (action.payload?.field === "taxData") {
            if (action.payload.value?.hasOwnProperty("taxDataOverride")) {
              (updatedValue as any).taxDataOverride =
                action.payload.value.taxDataOverride;
            } else {
              (updatedValue as any).taxDataOverride = true;
            }
          }

          return updatedValue;
        } else return el;
      });

      updatedBudget.baseValues = newBaseValues;
      break;

    case "INIT_BASE_VALUES": {
      // Usa o câmbio do evento se fornecido, caso contrário usa o padrão
      const exchangeToUse =
        action.payload?.eventExchange || baseValueDummy.exchange;
      const initVersion =
        action.versionInfo?.version ?? action.payload?.version ?? 0;

      // Nova categoria herda as taxas parametrizadas em Totais (budgetWideAppliedTaxes)
      // para a versão ativa, se houver alguma taxa com valor definido. Caso contrário,
      // a categoria nasce com as taxas zeradas (comportamento padrão).
      const globalTaxForVersion = state.budgetWideAppliedTaxes?.find(
        (el) => el.version === initVersion
      );
      const inheritedTaxData = hasAnyNonZeroTax(globalTaxForVersion?.taxData)
        ? { ...baseValueDummy.taxData, ...globalTaxForVersion?.taxData }
        : { ...baseValueDummy.taxData };

      updatedBudget.baseValues = [
        ...updatedBudget.baseValues,
        {
          ...baseValueDummy,
          taxData: inheritedTaxData,
          calcOption: action.payload?.calcOption,
          category: action.payload?.type,
          supplierId: action.payload?.supplierId,
          version: initVersion,
          exchange: exchangeToUse,
          isOverride: false, // Nova categoria sempre começa sem override
          taxDataOverride: false, // Nova categoria sempre começa sem customização manual de taxas
        } as any,
      ];
      break;
    }

    case "UPDATE_BASE_VALUES_EXCHANGE":
      if (
        action.payload?.eventExchange &&
        updatedBudget.baseValues &&
        updatedBudget.baseValues.length > 0
      ) {
        const forceUpdate = action.payload?.forceUpdate || false;

        updatedBudget.baseValues = updatedBudget.baseValues.map((baseValue) => {
          if (!forceUpdate && baseValue.isOverride) {
            return baseValue;
          }

          return {
            ...baseValue,
            exchange: {
              date: action.payload.eventExchange.date,
              currency: action.payload.eventExchange.currency,
              value: action.payload.eventExchange.value,
            },
            isOverride: false,
          };
        });
      }
      break;

    case "UPDATE_PAYMENT_INFO":
      const newPaymentInfoArr = updatedBudget.paymentSolicitation.map((el) => {
        if (
          el.supplierId === action.payload.supplierId &&
          el.type === action.payload.type &&
          el.calcOption === action.payload.calcOption
        ) {
          return action.payload;
        }
        return el;
      });
      updatedBudget.paymentSolicitation = newPaymentInfoArr;
      toast.success("Solicitação de pagamento atualizada!");

      break;
    case "CREATE_PAYMENT_INFO":
      updatedBudget.paymentSolicitation.push({
        ...action.payload,
        _id: uuid(),
      });
      toast.success("Solicitação de pagamento criada!");

      break;
    case "UPDATE_ANEX_FILES":
      const newFilesArr = updatedBudget.paymentSolicitation.map((el) => {
        if (
          el.supplierId === action.payload.supplierId &&
          el.type === action.payload.type &&
          el.calcOption === action.payload.calcOption
        ) {
          return action.payload;
        }
        return el;
      });
      updatedBudget.paymentSolicitation = newFilesArr;
      toast.success("Solicitação de pagamento atualizada!");

      break;
    case "CREATE_ANEX_FILES":
      updatedBudget.paymentSolicitation.push(action.payload.paymentInfo);
      toast.success("Solicitação de pagamento criada!");

      break;
    case "UPDATE_FROM_API":
      updatedBudget = withSupplierUids(action.payload);
      break;
    case "DELETE_BUDGET_ITEM": {
      // Ao deletar um budgetItem, todos serviços dele também serão apagados!
      // MMV-2419-9: negociação E fechamento (version > 0) usam o MESMO soft-delete —
      // esconde a categoria da etapa (addedItemTypeList) e remove a versão atual de
      // service.versions, preservando os serviços no orçamento base. O comparativo filtra
      // por `versions` (allServices), então a categoria aparece zerada com savings na etapa.
      const currentItemVersion = action.versionInfo?.version ?? 0;
      const isVersionedItemStage =
        currentItemVersion > 0 &&
        (action.versionInfo?.status === "NEGOTIATE" ||
          action.versionInfo?.status === "APPROVED" ||
          action.versionInfo?.status === "APROVED");

      if (isVersionedItemStage) {
        // `addedItemTypeList` também NÃO é versionado (ver DELETE_SUPPLIER): removê-lo
        // aqui apagava a categoria do orçamento original junto com a etapa atual. A
        // categoria some da etapa porque nenhum serviço dela tem a versão atual — a
        // renderização deriva isso de `service.versions`.
        //
        // Categoria ainda VAZIA não tem serviço nenhum de onde derivar isso, então a
        // exclusão fica registrada na própria entrada (mesmo contrato do fornecedor).
        updatedBudget.addedItemTypeList = updatedBudget.addedItemTypeList.map(
          (el) =>
            el.supplierId === action.payload.supplierId &&
            el.type === action.payload.type &&
            el.calcOption === action.payload.calcOption
              ? {
                  ...el,
                  deletedInVersions: Array.from(
                    new Set([
                      ...(el.deletedInVersions ?? []),
                      currentItemVersion,
                    ])
                  ),
                }
              : el
        );
        // MMV-2659: casa pela forma de cobrança EM VIGOR NA ETAPA, não pela
        // identidade `el.calcOption`. Depois do MMV-2607 o serviço movido
        // mantém a identidade da origem e só `closingValues.calcOption` (ou
        // `negotiationValue[].calcOption`) aponta o destino: comparar pela
        // identidade não casava nenhum serviço da categoria movida, então a
        // entrada de `addedItemTypeList` ficava marcada como excluída (a
        // categoria sumia da tela) enquanto os serviços seguiam com a versão
        // — e o export continuava listando as linhas. Pelo mesmo motivo, o
        // serviço que SAIU deste card (identidade daqui, movido para outra
        // forma) não pode mais perder a versão junto.
        updatedBudget.budgetServices = updatedBudget.budgetServices.map(
          (el) => {
            if (
              el.supplierId === action.payload.supplierId &&
              el.type === action.payload.type &&
              resolveServiceCalcOption(el, action.versionInfo) ===
                action.payload.calcOption
            ) {
              return {
                ...el,
                versions: (el.versions ?? []).filter(
                  (v) => v !== currentItemVersion
                ),
              };
            }
            return el;
          }
        );
      } else {
        updatedBudget.addedItemTypeList =
          updatedBudget.addedItemTypeList.filter(
            (el) =>
              !(
                el.supplierId === action.payload.supplierId &&
                el.type === action.payload.type &&
                el.calcOption === action.payload.calcOption
              )
          );
        updatedBudget.budgetServices = updatedBudget.budgetServices.filter(
          (el) =>
            !(
              el.supplierId === action.payload.supplierId &&
              el.type === action.payload.type &&
              el.calcOption === action.payload.calcOption
            )
        );
        updatedBudget.baseValues = updatedBudget.baseValues.filter(
          (el) =>
            !(
              el.supplierId === action.payload.supplierId &&
              el.category === action.payload.type &&
              el.calcOption === action.payload.calcOption &&
              el.version === action.versionInfo?.version
            )
        );
      }

      break;
    }
    case "UPDATE_MAIN_APPLIED_TAXES": {
      updatedBudget.mainAppliedTaxes = updatedBudget.mainAppliedTaxes.map(
        (el) => {
          if (
            el.supplierId === action.payload.supplierId &&
            el.category === action.payload.category &&
            el.calcOption === action.payload.calcOption &&
            el.version === action.payload.version
          ) {
            return action.payload;
          }
          return el;
        }
      );

      updatedBudget.budgetServices = updatedBudget.budgetServices.map((el) => {
        if (
          el.supplierId !== action.payload.supplierId ||
          el.type !== action.payload.category ||
          el.calcOption !== action.payload.calcOption ||
          !(
            el.versions?.includes(action.payload.version) ||
            action.payload.version === 0
          )
        ) {
          return el;
        }

        // Sempre sincroniza: não há como distinguir de forma confiável uma
        // customização manual real de um serviço que só ficou desatualizado
        // em relação ao padrão (ex.: taxa nunca sincronizada antes). Alterar
        // "Taxas padrão" deve refletir em todos os serviços da categoria.
        const syncAppliedTaxesIfInherited = (
          serviceValues: ServiceValues
        ): ServiceValues => ({
          ...serviceValues,
          appliedTaxes: action.payload.appliedTaxes,
        });

        const newService = { ...el };
        if (action.versionInfo?.status === "APPROVED") {
          newService.closingValues = syncAppliedTaxesIfInherited(
            newService.closingValues ?? newService.budgetValues
          );
        } else if (action.payload.version === 0) {
          newService.budgetValues = syncAppliedTaxesIfInherited(
            newService.budgetValues
          );
        } else {
          newService.negotiationValue = [
            ...(newService.negotiationValue ?? []),
          ];
          const currentNegotiationValue =
            newService.negotiationValue[action.payload.version - 1] ??
            newService.budgetValues;
          newService.negotiationValue[action.payload.version - 1] =
            syncAppliedTaxesIfInherited(currentNegotiationValue);
        }
        return newService;
      });
      break;
    }

    case "SET_TYPE":
      updatedBudget.type = action.payload.type;
      break;

    case "UPDATE_SUPPLIER_DESCRIPTIONS":
      updatedBudget.supplierList = updatedBudget.supplierList.map((el) => {
        if (
          el.supplierId === action.payload.supplierId &&
          el.calcOption === action.payload.calcOption
        ) {
          return {
            ...el,
            descriptions: action.payload.descriptions,
          };
        }
        return el;
      });
      break;

    case "INIT_MAIN_APPLIED_TAXES":
      const newMainAppliedTaxes = {
        appliedTaxes: {
          serviceTax: true,
          iss: true,
          cbs: true,
          ibs: true,
          tribute: true,
          supplier_tribute: true,
          localTax: true,
          tourismTax: true,
          fee: true,
          iof: true,
          markup: true,
          overhead: true,
          commission: true,
          incentive: true,
          doubleCharged: true,
        },
        calcOption: action.payload?.calcOption,
        category: action.payload?.category,
        supplierId: action.payload?.supplierId,
        version: action.payload?.version,
      };
      updatedBudget.mainAppliedTaxes?.length > 0
        ? (updatedBudget.mainAppliedTaxes = [
            ...updatedBudget.mainAppliedTaxes,
            newMainAppliedTaxes,
          ])
        : (updatedBudget.mainAppliedTaxes = [newMainAppliedTaxes]);
      break;
    default:
      break;
  }

  // para qualquer uma dessas ações versionInfo deve ser fornecida
  const actionCalc = [
    "UPDATE_SERVICES_EXCHANGE",
    "UPDATE_BASE_VALUES_EXCHANGE", // Dispara recálculo quando câmbio das categorias muda
    "DELETE_SERVICE",
    "DELETE_BUDGET_ITEM",
    "DELETE_SUPPLIER",
    "UPDATE_SERVICE",
    "UPDATE_BASE_CALC_VALUES",
    "UPDATE_CALC_VALUES",
    "UPDATE_MAIN_APPLIED_TAXES",
    "NEW_GLOBAL_TAX_DATA",
    "MOVE_CATEGORY",
    // Os totais por fornecedor derivam de `budgetServices`; reescrever o
    // `supplierId` já reposiciona os valores entre origem e destino, e
    // `updateCalcValues` reconstrói o agregado da etapa do zero.
    "TRANSFER_CATEGORY_SUPPLIER",
    // Além de reposicionar o valor entre origem e destino, o recálculo é o que
    // reaplica o perfil fiscal da categoria de destino ao serviço transferido e
    // redistribui as taxas de valor fixo (rateadas por `numberOfServices`)
    // entre os serviços que ficaram na origem.
    "TRANSFER_SERVICE_SUPPLIER",
    "UPDATE_SUPPLIER_LIST", // Dispara recálculo quando fornecedor é alterado para atualizar totais com novo supplierId
    // Agrupamento: só as duas que mexem no CONJUNTO de serviços entram aqui.
    //
    // `DUPLICATE_GROUP` insere uma cópia de cada serviço do agrupamento, e
    // `REMOVE_ROW_FROM_GROUP` apaga os de uma linha — nos dois casos o total
    // muda. Sem o recálculo o rodapé ficava no valor antigo até o usuário
    // recarregar a página: retorno do QA, "dupliquei e ele não somou aqui no
    // total... a gente precisa de uma forma de refresh".
    //
    // `CREATE_GROUP`, `RENAME_GROUP`, `REORDER_GROUPS`, `DELETE_GROUP` e
    // `MOVE_ROW_TO_GROUP` NÃO entram: mexem em nome, ordem e vínculo
    // (`groupId`), nunca em valor. Recalcular ali seria varrer todos os
    // serviços à toa a cada arrastar.
    "DUPLICATE_GROUP",
    "REMOVE_ROW_FROM_GROUP",
    "RECALCULATE_TOTALS", // Reparo de agregado legado: só recalcula, não altera dado
    // MMV-2511-X: categoria sem `baseValues` para a versão ativa nasce zerada
    // (fallback no dummy all-zero em `calculateAndMapService`) até alguém
    // editar algo. `INIT_BASE_VALUES` já cria a linha que falta — faltava
    // recalcular em cima dela. Sem isto, Taxa MM/Imposto definidos só no
    // serviço (sem contraparte em `baseValues` na versão aberta) ficavam
    // zerados no cabeçalho da categoria mesmo depois da linha ser criada:
    // os campos de "Valores" mostravam o herdado de Totais, mas o total
    // continuava com a foto de antes da linha existir.
    "INIT_BASE_VALUES",
  ];
  let shouldRecalculateTotals = actionCalc.includes(action.type);
  // Both UPDATE_SERVICE and UPDATE_CALC_VALUES carry a full ServiceData payload,
  // so the metadata-only short-circuit applies to both. Without this, typing in
  // observation/serviceType/description fields (which dispatch UPDATE_CALC_VALUES)
  // re-runs the full O(N) per-service calc + O(N) totals walk on every keystroke.
  if (
    (action.type === "UPDATE_SERVICE" ||
      action.type === "UPDATE_CALC_VALUES") &&
    shouldRecalculateTotals &&
    shouldSkipBudgetRecalcForMetadataOnlyServiceUpdate(state, action.payload)
  ) {
    shouldRecalculateTotals = false;
  }

  if (shouldRecalculateTotals) {
    // Prefer scoping line recalc to the touched category group. A full-budget
    // `calculateAndMapService` pass rewrites every line with current
    // baseValues/exchange rules — so editing ROOMS could change AEB totals
    // whenever stored service exchange (or tax snapshots) diverged from
    // baseValues. Totals-tree assembly still walks all services below.
    const updatedBudgetCalc = updateCalcValues(
      updatedBudget,
      action.versionInfo || {
        comparisonVersion: 0,
        version: 0,
        status: "OPEN",
      },
      resolveLineCalcScope(action, state)
    );
    return clearStaleApiUpdateFlag(updatedBudgetCalc, action);
  }

  // `updatedBudget` is already a fresh top-level reference (see `let updatedBudget = { ...state }` above).
  // Action handlers that touch `baseValues` / `budgetServices` already replace those arrays with fresh refs.
  // We intentionally avoid `baseValues.map(bv => ({ ...bv }))` here: it used to fire on every action
  // (including every keystroke) and wastefully invalidated downstream memos keyed on baseValue identity.
  return clearStaleApiUpdateFlag(updatedBudget, action);
}
function shouldApplyExchange(
  exchange: ExchangeData,
  compareExchange: ExchangeData | undefined
) {
  return (
    (exchange.value !== 0 && exchange.value !== compareExchange?.value) ||
    exchange.date !== compareExchange?.date ||
    exchange.currency !== compareExchange?.currency ||
    compareExchange === undefined
  );
}

/** Category group identity for scoped line recalculation. */
type LineCalcScope = {
  supplierId: string;
  type: string;
  calcOption: string;
};

/**
 * Returns a category-group scope for actions that only touch one group, or
 * `null` to recalculate every service (global tax/exchange/move/reparo).
 *
 * Uses `prevState` for DELETE_SERVICE because the line may already be gone
 * from `updatedBudget.budgetServices` when we resolve the scope.
 */
function resolveLineCalcScope(
  action: ActiveBudgetAction,
  prevState: Budget
): LineCalcScope | null {
  switch (action.type) {
    case "UPDATE_SERVICE":
    case "UPDATE_CALC_VALUES":
    case "ADD_SERVICE": {
      const service = action.payload as ServiceData | undefined;
      if (!service?.supplierId || !service?.type || !service?.calcOption) {
        return null;
      }
      return {
        supplierId: service.supplierId,
        type: service.type,
        calcOption: service.calcOption,
      };
    }
    case "DELETE_SERVICE": {
      const prev = prevState.budgetServices?.find(
        (el) => el._id === action.payload
      );
      if (!prev?.supplierId || !prev?.type || !prev?.calcOption) return null;
      return {
        supplierId: prev.supplierId,
        type: prev.type,
        calcOption: prev.calcOption,
      };
    }
    case "UPDATE_BASE_CALC_VALUES":
    case "INIT_BASE_VALUES": {
      const payload = action.payload as {
        supplierId?: string;
        type?: string;
        category?: string;
        calcOption?: string;
      };
      const type = payload?.type ?? payload?.category;
      if (!payload?.supplierId || !type || !payload?.calcOption) return null;
      return {
        supplierId: payload.supplierId,
        type,
        calcOption: payload.calcOption,
      };
    }
    case "UPDATE_MAIN_APPLIED_TAXES":
    case "INIT_MAIN_APPLIED_TAXES": {
      const payload = action.payload as {
        supplierId?: string;
        category?: string;
        calcOption?: string;
      };
      if (!payload?.supplierId || !payload?.category || !payload?.calcOption) {
        return null;
      }
      return {
        supplierId: payload.supplierId,
        type: payload.category,
        calcOption: payload.calcOption,
      };
    }
    default:
      return null;
  }
}

function serviceMatchesLineCalcScope(
  service: ServiceData,
  scope: LineCalcScope
): boolean {
  return (
    service.supplierId === scope.supplierId &&
    service.type === scope.type &&
    service.calcOption === scope.calcOption
  );
}

/**
 * Recalculate line items + assemble totals trees for the active phase.
 *
 * Line-level math (`calculateAndMapService` / `calculateServiceTotals`) is the
 * instant-feedback path and must stay for editor UX while PATCH round-trips.
 *
 * When `scope` is set, only services in that category group are remapped —
 * siblings in other categories keep their stored calculated slices so a ROOMS
 * edit cannot rewrite AEB via divergent baseValues/exchange. The totals tree
 * is always rebuilt from the full service list.
 *
 * Totals-tree assembly (`calculateDetailedTotals*`) is **display-only** when
 * `USE_SERVER_TOTALS` is on (FE-P4-02): optimistic card/sidebar preview until
 * FE-P4-01 applies server slices. Those trees are stripped from save payloads.
 *
 * Phase 5 cleanup (post X-P4-01): remove client totals-tree assembly once
 * display can rely on server round-trips (or a lighter preview). Keep per-line
 * calc. See FE-P4-02 / backlog Phase 5 cleanup.
 */
function updateCalcValues(
  updatedBudget: Budget,
  versionInfo: versionInfo,
  scope: LineCalcScope | null = null
) {
  const budgetCalculated = updatedBudget;
  budgetCalculated.budgetServices = budgetCalculated.budgetServices.map(
    (service) => {
      if (scope && !serviceMatchesLineCalcScope(service, scope)) {
        return service;
      }
      return calculateAndMapService(service, budgetCalculated, versionInfo);
    }
  );
  const extras =
    updatedBudget.budgetWideAppliedTaxes?.find(
      (el) => el.version === versionInfo.version
    )?.taxData.extras || zeroTax;

  // Órfãos (serviço sem linha em supplierList) e linhas fora da etapa não
  // entram nos totais — o mesmo contrato do editor. `undefined` supplierList
  // não aplica pertinência (ver isServiceVisibleInStage).
  //
  // MMV-2626-2: a pertinência é no card da forma DA ETAPA. Um serviço movido
  // B→A no fechamento sem card do fornecedor em A não aparece no editor, mas
  // pela forma do orçamento (B) ele entrava na árvore — e o `SUM` gravado é o
  // que a lista de orçamentos mostra nas linhas que não estão abertas.
  const servicesInStage = (budgetCalculated.budgetServices ?? []).filter(
    (service) =>
      isServiceVisibleInStage(
        service,
        budgetCalculated.supplierList,
        versionInfo.version,
        resolveServiceCalcOption(service, versionInfo)
      )
  );

  // --- Display-only totals trees (FE-P4-02); not sent on save when flag on ---
  switch (true) {
    case versionInfo.status === "APPROVED":
      //closing
      // MMV-2419-10: filtra por `versions` (igual à negociação) para o total de fechamento
      // não incluir serviços excluídos nesta etapa. Sem isso, `closingValuesTotal` (fonte
      // do resumo de economia por categoria) continuava somando o serviço removido, enquanto
      // a comparação (negociação) já o excluía → falso "aumento de escopo".
      budgetCalculated.closingValuesTotal = calculateDetailedTotals(
        servicesInStage,
        "closingValues",
        extras
      );
      break;
    case versionInfo.status === "NEGOTIATE":
      // negotiation — replace the array slot immutably so React/context see a
      // new `negociationValuesTotal` reference and category savings refresh.
      {
        const negotiationTotals = calculateDetailedTotalsNegotiation(
          servicesInStage,
          versionInfo,
          extras
        );
        const negotiationIndex = versionInfo.version - 1;

        if (negotiationIndex >= 0) {
          const existing = budgetCalculated.negociationValuesTotal ?? [];
          if (negotiationIndex < existing.length) {
            budgetCalculated.negociationValuesTotal = existing.map(
              (entry, index) =>
                index === negotiationIndex ? negotiationTotals : entry
            );
          } else {
            budgetCalculated.negociationValuesTotal = [
              ...existing,
              negotiationTotals,
            ];
          }
        }
      }
      break;

    case versionInfo.version === 0 && versionInfo.status !== "APPROVED":
    default:
      // budget
      const total = calculateDetailedTotals(
        servicesInStage,
        "budgetValues",
        extras
      );
      budgetCalculated.budgetValuesTotal = total;
      break;
  }

  return updatedBudget;
}
export const zeroTax = {
  typeTax: "PERCENTAGE",
  value: 0,
  currencyValue: 0,
  valueBRL: 0,
  typeCalculate: "",
};

export const baseValueDummy = {
  version: 0,
  supplierId: "",
  category: "", // substitua com 'BudgetItemType.EXEMPLO' se tiver enums definidos
  calcOption: "A", // substitua com 'CalcOption.EXEMPLO' se tiver enums definidos
  exchange: { currency: "BRL", date: "", value: 1 }, // preencha conforme a estrutura real de ExchangeData
  taxData: {
    tariff: { ...zeroTax },
    serviceTax: { ...zeroTax },
    localTax: { ...zeroTax },
    tourismTax: { ...zeroTax },
    iof: { ...zeroTax },
    fee: { ...zeroTax },
    iss: { ...zeroTax },
    cbs: { ...zeroTax },
    ibs: { ...zeroTax },
    typeNfMm: "",
    overhead: { ...zeroTax },
    markup: { ...zeroTax },
    commission: { ...zeroTax },
    incentive: { ...zeroTax },
    tribute: { ...zeroTax },
    doubleCharged: { ...zeroTax },
  },
  descriptions: {
    composition: "",
    cancelPolicy: "",
    paymentMethod: "",
    commercialConditions: false,
  },
};
